import { z } from "zod";

import { annotScaleContextSchema } from "./annotative";
import { entityColorSchema } from "./color";
import {
  arrayAssocItemSchema,
  arrayItemOverrideSchema,
  arrayItemSourceMappingSchema,
  arrayItemTransformSchema,
  arrayReplacedItemSchema,
  assocArrayCarrierObjectSchema,
  assocArrayHandlesSchema,
  assocArrayParamsSnapshotSchema,
} from "./arrayAssoc";
import { blockAuthoringDataSchema } from "./blockAuthoring";
import { dynamicBlockMetadataSchema } from "./dynamicBlock";
import {
  drawingEntityBaseSchema,
  drawingPoint3DSchema,
  drawingPointOptionalZSchema,
  drawingPointSchema,
} from "./entityBase";
import {
  oleFrameEntitySchema,
  polylineEntitySchema,
  tableEntitySchema,
} from "./entityPolylineTableOle";
import { layoutPlotSettingsSchema } from "./layoutPlotSettings";
import { linetypeSchema, type Linetype } from "./linetype";
import { STANDARD_LINETYPES } from "./linetypeSeed";

/**
 * Fresh, independent deep copy of the standard acad.lin/ISO seed with every
 * entry tagged `source:"seed"` (FIX-4 — Codex#2 PR-LT2 P2). The tag lets the
 * DXF exporter skip UNUSED synthetic-seed linetypes on a plain round-trip
 * (AutoCAD's LTYPE table holds only DEFINED + USED linetypes), while the seed
 * stays in `document.linetypes` so the linetype refcheck resolves the standard
 * names (PR-LT1). A new clone per call keeps each document's table independent
 * (a future in-place Linetype Manager edit must not corrupt the module const).
 */
/**
 * Clamp a $MIRRTEXT value to AutoCAD's strict 0|1 boolean toggle (Codex #342
 * F4): 0 stays 0, any non-zero (incl. an out-of-range 2 or a negative) becomes
 * 1, an absent/undefined value falls back to the AutoCAD default 0.
 *
 * Single source of truth shared by the model `drawingDocumentSchema.mirrtext`
 * `.transform` (the Zod-walk path) AND the bridge import identity-cast path
 * (`dxfBridge.import.ts`, which skips the Zod walk) — so a raw `$MIRRTEXT=2`
 * lands as 1 regardless of which import path materialises the document, keeping
 * the document field, the live sysvar validator (0|1 only) and the export
 * consistent. Clamp-non-zero→1 (vs reset-to-0) preserves the user's
 * "mirror was on" intent, faithful to AutoCAD's boolean semantics.
 */
export function clampMirrtext(value: number | undefined): number {
  return value === undefined || value === 0 ? 0 : 1;
}

/**
 * The AutoCAD FILLMODE default — the value an absent `$FILLMODE` materialises.
 *
 * Autodesk system-variable reference, FILLMODE: "Specifies whether hatches and
 * fills, 2D solids, and wide polylines are filled in" — type Integer, INITIAL
 * VALUE **1**, saved in the DRAWING. `ezdxf`'s own header-var table agrees
 * exactly (`sections/headervars.py`: `$FILLMODE`, code 70, `default=1`,
 * DXF12-2018), which is why an absent var must NOT fall back to 0: 0 is a real
 * state (draw the outline only), and defaulting to it would turn every legacy
 * DXF that simply omits the var into an outlined drawing.
 */
export const DEFAULT_FILLMODE = 1;

/**
 * Clamp a `$FILLMODE` value to AutoCAD's strict 0|1 toggle, the same shape
 * {@link clampMirrtext} enforces for `$MIRRTEXT` and for the same three
 * reasons: the Zod-walk path and the bridge's identity-cast import path must
 * agree, the live 0|1 validator must accept what the document carries, and the
 * export must re-emit a legal value.
 *
 * The one difference is the ABSENT case: `$MIRRTEXT` defaults to 0 and
 * `$FILLMODE` defaults to {@link DEFAULT_FILLMODE} = 1. Non-zero → 1 preserves
 * "fill was on" exactly as the MIRRTEXT clamp preserves "mirror was on".
 */
export function clampFillmode(value: number | undefined): number {
  if (value === undefined) return DEFAULT_FILLMODE;
  return value === 0 ? 0 : 1;
}

function seededStandardLinetypes(): Record<string, Linetype> {
  const seed = structuredClone(STANDARD_LINETYPES) as Record<string, Linetype>;
  for (const lt of Object.values(seed)) lt.source = "seed";
  return seed;
}

// Block-edit / block-test / refedit are transient space kinds introduced
// in Phase 2.3 (BEDIT / BSAVE / BCLOSE), Phase DYN-1.9 (TESTBLOCK), and
// Phase FIN.1 (REFEDIT) respectively. None of them is persisted in DXF —
// the serializer only writes "model" and "layout" kinds to disk.
// In memory:
//   "block-edit"  -> active BEDIT session; writes route to blocks[name].entities
//   "block-test"  -> TESTBLOCK sandbox canvas (DYN-1.9)
//   "refedit"     -> REFEDIT in-place block editing session (FIN.1).
//                    Surrounding (out-of-working-set) entities of the host
//                    space stay visible but tinted while edits route to
//                    blocks[refeditBlockName].entities.
export const drawingSpaceKindSchema = z.enum([
  "model",
  "layout",
  "block-edit",
  "block-test",
  "refedit",
]);

// R17 A08A-MODEL-SCHEMA-BARREL — compatibility barrel. `drawingPointSchema`,
// `drawingPoint3DSchema`, `drawingPointOptionalZSchema` and
// `drawingEntityBaseSchema` now live in `./entityBase` (imported above for
// local use throughout this file); re-exported here so every existing
// `model.ts` import path keeps resolving unchanged.
export {
  drawingEntityBaseSchema,
  drawingPoint3DSchema,
  drawingPointOptionalZSchema,
  drawingPointSchema,
} from "./entityBase";

export const lineEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Line"),
  // DXF LINE carries 3D endpoints (group 10/20/30 start, 11/21/31 end).
  // z is optional in the model (drawingPointOptionalZSchema) so the many
  // 2D `{x, y}` literals built by reducers / compact store / tests keep
  // typechecking; the bridge preserves a real z across the round-trip via
  // toDrawingPoint3D (import) / toDxfPoint3D (export, z ?? 0) — DEFUSE S1b.
  start: drawingPointOptionalZSchema,
  end: drawingPointOptionalZSchema,
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). Stored as an
  // *optional* field (S-EXTRUSION-STORE): absent ⇒ the (0,0,1) default;
  // present ⇒ a non-default OCS normal that survives the import→model→export
  // round-trip. `.optional()` (not `.default()`) keeps the thousands of bare
  // `{x,y}`-literal Line constructors typechecking unchanged. The OCS→WCS
  // render transform is a SEPARATE later slice — this slice only stores the
  // field; the renderer does not yet apply it.
  extrusion: drawingPoint3DSchema.optional(),
});

// AutoCAD POINT entity (AcDbPoint). Stored as a single (x, y) location
// plus an optional UCS X-axis rotation — DXF group 50, DEGREES (group-code
// -value-types spec + ezdxf; imported + exported verbatim, like TEXT/MText
// rotation) — used by PDMODE marker
// shapes (X, +, vertical tick — see system variable PDMODE). Renderer
// approximates PDMODE 0 (single dot) as a small SVG circle; full
// PDMODE shape support is feature work tracked separately.
export const pointEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Point"),
  // DXF POINT carries a 3D location (group 10/20/30). z is optional in the
  // model (drawingPointOptionalZSchema) so the 2D `{x, y}` literals built
  // by reducers / compact store / tests keep typechecking; the bridge
  // preserves a real z across the round-trip via toDrawingPoint3D (import)
  // / toDxfPoint3D (export, z ?? 0) — DEFUSE S1b.
  location: drawingPointOptionalZSchema,
  rotation: z.number().default(0),
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). Optional store
  // (S-EXTRUSION-STORE); absent ⇒ (0,0,1) default. OCS→WCS render is a later
  // slice — this slice only round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
});

export const circleEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Circle"),
  // DXF CIRCLE carries a 3D center (group 10/20/30). z is optional in the
  // model (drawingPointOptionalZSchema) so the 2D `{x, y}` literals built
  // by reducers / compact store / tests keep typechecking; the bridge
  // preserves a real z across the round-trip via toDrawingPoint3D (import)
  // / toDxfPoint3D (export, z ?? 0) — DEFUSE S-ARC-CIRCLE-CENTER-Z.
  center: drawingPointOptionalZSchema,
  radius: z.number().positive(),
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). Optional store
  // (S-EXTRUSION-STORE); absent ⇒ (0,0,1) default. OCS→WCS render is a later
  // slice — this slice only round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
});

export const arcEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Arc"),
  // DXF ARC carries a 3D center (group 10/20/30). z is optional in the
  // model (drawingPointOptionalZSchema) so the 2D `{x, y}` literals built
  // by reducers / compact store / tests keep typechecking; the bridge
  // preserves a real z across the round-trip via toDrawingPoint3D (import)
  // / toDxfPoint3D (export, z ?? 0) — DEFUSE S-ARC-CIRCLE-CENTER-Z.
  center: drawingPointOptionalZSchema,
  radius: z.number().positive(),
  startAngle: z.number(),
  endAngle: z.number(),
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). Optional store
  // (S-EXTRUSION-STORE); absent ⇒ (0,0,1) default. OCS→WCS render is a later
  // slice — this slice only round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
});

/* ── ELLIPSE ──────────────────────────────────────── */
// Full-fidelity ELLIPSE entity. AutoCAD stores an ellipse as a center
// point plus a major-axis end-point vector (relative to center), a
// minor-to-major ratio, and start/end parameters in radians measured
// CCW from the major axis. Full ellipse: startParam=0, endParam=2π.
// Ellipse arc: any other start/end. Rotation of the major axis is
// implicit in the (x, y) of majorAxisEndpoint (atan2). Required for
// EXPLODE non-uniform Circle/Arc → ellipse and for direct DXF round-
// trip support.
//
// SL-15 — center (DXF 10/20/30) and majorAxisEndpoint (11/21/31) are FULL
// 3D (WCS): an elevated or out-of-XY-plane ellipse carries a real z on the
// center and/or the major-axis vector. Stored via `drawingPointOptionalZSchema`
// (z optional) — IDENTICAL to Circle/Arc `center` (S-ARC-CIRCLE-CENTER-Z) — so
// the pervasive 2D `{x, y}` construction literals (reducers / EXPLODE / AI
// builders draw planar ellipses) keep typechecking while an imported z rides
// through. The bridge exports `z ?? 0` (writer always emits 30/31, matching
// ezdxf `ellipse.py` center/major_axis = Vec3). Previously `drawingPointSchema`
// (2D) → the z was hard-dropped at the model boundary (flattened to XY).
export const ellipseEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Ellipse"),
  center: drawingPointOptionalZSchema,
  majorAxisEndpoint: drawingPointOptionalZSchema, // relative to center (vector); z tilts the plane
  ratio: z.number().positive(), // minor/major (0, 1]
  startParam: z.number().default(0), // 0..2π
  endParam: z.number().default(2 * Math.PI),
  extrusion: drawingPoint3DSchema.default({ x: 0, y: 0, z: 1 }),
  thickness: z.number().default(0),
});

/* ── SPLINE ───────────────────────────────────────── */
// Full-fidelity SPLINE entity (AcDbSpline). AutoCAD stores splines in
// one of two construction modes:
//   - **Fit-points mode**: user provides points the curve must pass
//     through; control points are derived via cubic interpolation.
//     Optional start/end tangent vectors clamp the derivatives.
//   - **Control-points mode** (NURBS): user provides degree, knot
//     vector, control points, and (optionally) per-control-point
//     weights. The curve approximates the control polygon.
//
// DXF group codes:
//   100 AcDbSpline subclass marker
//    70 flags (1=closed, 2=periodic, 4=rational, 8=planar, 16=linear)
//    71 degree (typically 3 = cubic)
//    72 number of knots
//    73 number of control points
//    74 number of fit points
//    42 knot tolerance (default 1e-7)
//    43 control-point tolerance (default 1e-10)
//    44 fit tolerance (default 1e-10)
//   12/22/32 start tangent (relative to first fit point)
//   13/23/33 end tangent
//    40 (multiple) knot value
//    41 (multiple) weight (default 1.0 per CP)
//   10/20/30 (multiple) control point
//   11/21/31 (multiple) fit point
export const splineEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Spline"),
  degree: z.number().int().positive().default(3),
  closed: z.boolean().default(false),
  periodic: z.boolean().default(false),
  rational: z.boolean().default(false),
  planar: z.boolean().default(true),
  // DXF group 70 bit 16 — linear spline (all control points collinear /
  // a straight degree-1 segment chain). Parsed by dxf-io ((flags & 16))
  // but previously hardcoded false on the export bridge. Optional store
  // (S-SPLINE); absent ⇒ false.
  linear: z.boolean().optional(),
  // SL-15 — DXF group 210/220/230, the SPLINE normal / OCS extrusion vector
  // ("Normal vector (omitted if nonplanar)", Autodesk R2018; ezdxf spline.py
  // `extrusion = DXFAttr(210, xtype=XType.point3d, default=Z_AXIS,
  // optional=True)`). The control points remain full 3D WCS, so this only
  // preserves the plane ORIENTATION of a planar spline authored in a tilted
  // UCS (a WCS/nonplanar spline omits it). Optional — absent ⇒ the (0,0,1)
  // WCS default, keeping the model compact. Rides the generic common-extrusion
  // channel both ways (projectCommonEntityProps export / dxfSplineToDrawingSpline
  // import), like Line/Circle/LWPOLYLINE (S-EXTRUSION-STORE).
  extrusion: drawingPoint3DSchema.optional(),
  // Fit-points mode (AutoCAD's SPLINE FIT entry path)
  fitPoints: z.array(drawingPoint3DSchema).default([]),
  startTangent: drawingPoint3DSchema.optional(),
  endTangent: drawingPoint3DSchema.optional(),
  // Control-points mode (AutoCAD's SPLINE CV entry path)
  controlPoints: z.array(drawingPoint3DSchema).default([]),
  knots: z.array(z.number()).default([]),
  weights: z.array(z.number()).default([]),
  // Tolerances per AutoCAD DXF reference
  knotTolerance: z.number().default(1e-7),
  controlPointTolerance: z.number().default(1e-10),
  fitTolerance: z.number().default(1e-10),
});

/* ── LWPOLYLINE ─────────────────────────────────── */
export const polylineVertexSchema = z.object({
  x: z.number(),
  y: z.number(),
  bulge: z.number().default(0),
  startWidth: z.number().default(0),
  endWidth: z.number().default(0),
  // DXF group 91 — persistent vertex identifier (AcDbPolyline body).
  // Optional because legacy fixtures / purely-static blocks never carry
  // the 91 code. Present on AutoCAD 2018+ constraint-based blocks where
  // ACDBASSOCGEOMDEPENDENCY records reference vertex subent-IDs.
  vertexId: z.number().int().optional(),
});

export const lwpolylineEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("LWPOLYLINE"),
  vertices: z.array(polylineVertexSchema).min(2),
  closed: z.boolean().default(false),
  // DXF group 70 bit 128 — plinegen (continuous linetype generation across
  // vertices, vs. dash pattern restarting at each vertex). Parsed by dxf-io
  // but previously dropped on import/export. Optional store (S-LWPOLYLINE);
  // absent ⇒ false. Lives alongside the closed bit (1) in the same 70 field.
  plinegen: z.boolean().optional(),
  constantWidth: z.number().default(0),
  elevation: z.number().default(0),
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). LWPOLYLINE is an
  // OCS entity (its 2D vertices live in the plane defined by this normal).
  // Optional store (S-EXTRUSION-STORE); absent ⇒ (0,0,1) default. OCS→WCS
  // render is a later slice — this slice only round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
});

/* ── TEXT ─────────────────────────────────────────── */
export const textEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Text"),
  insertionPoint: drawingPointSchema,
  alignmentPoint: drawingPointSchema.optional(),
  // DXF group 40: text height. AutoCAD writes 0 for annotative TEXT —
  // the visual size comes from the active annotation scale, not group 40.
  // Round-trip requires preserving any value the file supplies, including 0.
  // Negative still rejected.
  height: z.number().nonnegative(),
  rotation: z.number().default(0),
  contents: z.string(),
  styleName: z.string().default("Standard"),
  widthFactor: z.number().default(1),
  obliqueAngle: z.number().default(0),
  hJustification: z.number().int().default(0),
  vJustification: z.number().int().default(0),
  thickness: z.number().default(0),
  generationFlags: z.number().int().default(0),
  extrusion: drawingPoint3DSchema.default({ x: 0, y: 0, z: 1 }),
  // ── Annotative (PR-1) ──
  // Per-annotation-scale context snapshots from the OBJECTS-section
  // ACDB_ANNOTSCALEOBJECTCONTEXTDATA records linked via the entity's
  // extension dictionary. Empty when the TEXT is not annotative or only
  // has the implicit creation-scale context. Each entry mirrors the on-
  // disk `AcDbTextObjectContextData` record.
  annotativeContexts: z.array(annotScaleContextSchema).default([]),
  // Name of the annotation scale that was active when the entity was
  // created (mirrors AutoCAD's "creation scale" — used by ANNOAUTOSCALE
  // and ANNORESET to derive new contexts from the master fields).
  // Optional because legacy fixtures and non-annotative TEXT do not
  // carry it.
  creationScaleName: z.string().optional(),
});

/* ── MTEXT ────────────────────────────────────────── */
export const mtextEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("MText"),
  insertionPoint: drawingPointSchema,
  nominalHeight: z.number().positive(),
  // DXF group 41: reference rectangle width. AutoCAD writes 0 when the
  // MTEXT box is unlimited (no wrapping) — valid and appears in paper-space
  // template blocks. Accept 0 and positive; negative still rejected.
  referenceWidth: z.number().nonnegative(),
  attachmentPoint: z.number().int().default(1),
  drawingDirection: z.number().int().default(1),
  contents: z.string(),
  styleName: z.string().default("Standard"),
  rotation: z.number().default(0),
  lineSpacingStyle: z.number().int().default(1),
  lineSpacingFactor: z.number().default(1),
  backgroundFill: z.boolean().default(false),
  backgroundFillColor: z.string().optional(),
  columnType: z.number().int().default(0),
  columnCount: z.number().int().default(1),
  xAxisDirection: drawingPoint3DSchema.default({ x: 1, y: 0, z: 0 }),
  // DXF group 210/220/230 — OCS extrusion (handedness) normal. MTEXT geometry is
  // WCS, but it CARRIES an extrusion attribute that AutoCAD/ezdxf use to encode a
  // mirrored-text det=−1 handedness ((0,0,−1) ⇒ mirrored, see MIRRTEXT=1 in
  // command-engine `mirrorTextFull`). Stored *optional* (Codex #342 F3): absent ⇒
  // the (0,0,1) default (no group 210 emitted); present non-default ⇒ survives
  // import→model→export via the dxf-io COMMON 210 channel + the bridge.
  // `.optional()` (not `.default()`) keeps existing bare MText literals
  // typechecking unchanged.
  extrusion: drawingPoint3DSchema.optional(),
  backgroundFillSetting: z.number().int().default(0),
  fillBoxScale: z.number().default(1.5),
  backgroundFillColorACI: z.number().int().default(0),
  backgroundFillColorRGB: z.number().int().optional(),
  backgroundTransparency: z.number().int().default(0),
  columnFlowReversed: z.boolean().default(false),
  columnAutoheight: z.boolean().default(false),
  columnWidth: z.number().default(0),
  columnGutter: z.number().default(0),
  columnHeights: z.array(z.number()).default([]),
  horizontalWidth: z.number().default(0),
  verticalHeight: z.number().default(0),
  // ── Annotative (PR-1) ──
  // Per-annotation-scale context snapshots — see textEntitySchema for
  // the same field semantics. MTEXT contexts mirror
  // `AcDbMTextObjectContextData` plus the
  // `AcDbMTextAttributeObjectContextData` variant when the MTEXT is
  // serving as a multileader attribute.
  annotativeContexts: z.array(annotScaleContextSchema).default([]),
  creationScaleName: z.string().optional(),
});

/* ── ANNOTATIVE CONTEXT (Group C + B2) ────────────────── */
// Per-annotative-scale context data. AutoCAD stores a ContextData block
// for every annotation scale an annotative entity participates in. The
// context copies every visual-affecting field so the renderer can pick
// the proper entry based on state.drawing.annotationScale without
// recomputing anything at draw time. The common base is shared by
// DIMENSION + MULTILEADER; the MLEADER-specific fields
// (dogleg/landingGap/textRotation/textColor/textWidth) live as dedicated
// defaults on the MLEADER extension so existing round-trip shapes are
// unchanged.
export const annotativeContextSchema = z.object({
  // Annotation scale as a numeric ratio (e.g. 1:100 → 0.01). Matches
  // AutoCAD's DIMSCALE semantics for annotative contexts.
  scale: z.number().default(1),
  // Human-readable scale name (e.g. "1:100"). Empty when the DXF
  // didn't carry one — AutoCAD renders the numeric scale in that case.
  scaleName: z.string().default(""),
  // Core visual overrides — present for every annotative entity.
  textHeight: z.number().default(0),
  arrowheadSize: z.number().default(0),
  // Optional MLEADER-specific overrides — MLEADER persists these too.
  // Dimension contexts leave them undefined.
  doglegLength: z.number().optional(),
  landingGap: z.number().optional(),
  textRotation: z.number().optional(),
  textColor: z.string().optional(),
  textWidth: z.number().optional(),
});
export type AnnotativeContext = z.infer<typeof annotativeContextSchema>;

/* ── DIMENSION (all 6 subtypes) ───────────────────── */
export const dimensionEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Dimension"),
  dimensionType: z.number().int().default(0),
  defPoint: drawingPointSchema,
  textMidpoint: drawingPointSchema,
  dimensionText: z.string().default(""),
  styleName: z.string().default("Standard"),
  textHeight: z.number().default(0),
  textRotation: z.number().default(0),
  measurement: z.number().default(0),
  extLine1Point: drawingPointSchema.optional(),
  extLine2Point: drawingPointSchema.optional(),
  linearAngle: z.number().default(0),
  attachmentPoint: z.number().int().default(5),
  /* ── Phase 3: full-fidelity dimension fields ──── */
  thirdPoint: drawingPoint3DSchema.optional(), // code 15/25/35 (angular/radius/diameter)
  arcPoint: drawingPoint3DSchema.optional(), // code 16/26/36 (angular only)
  obliqueAngle: z.number().default(0), // code 51
  textAngleRelativeToDimLine: z.number().default(0), // code 53
  // GitHub Codex review PR #558 (post-rebase fresh pass) P1 — the
  // handedness companion to `textAngleRelativeToDimLine`, matching how
  // Multileader (`textMirrored`) and a block-attribute
  // (`overrideGenerationFlags`) both carry a MIRRTEXT=1 handedness state
  // alongside their bare rotation field (`mirrorRotationField.ts`'s module
  // doc — "these three don't need the TEXT/MTEXT machinery's extra
  // fields" no longer holds without this: a reflected dimension-text
  // ANGLE alone renders a normally-shaped, merely-rotated run, not a
  // determinant-negative glyph reflection).
  //
  // Issue #565 ("Dimension.textMirrored has no DXF persistence") — user-
  // escalated blocking requirement, resolved via DXF persistence (not
  // deferred): unlike TEXT/ATTRIB/ATTDEF's own literal group-71
  // text-generation-flags bit, AcDbDimension genuinely has NO native DXF
  // group for this (dimension text is AutoCAD-regenerated from DIMSTYLE +
  // measurement, not a stored glyph run) — confirmed via a widened research
  // pass (see the issue for the full writeup) before landing on a fix:
  // - The anonymous-dimension-block route (group 2, `anonymousBlockName`
  //   below) was ruled out: it is a PURE byte-preserve passthrough with
  //   ZERO regeneration logic anywhere in the mutation pipeline (confirmed
  //   via full-repo grep) — building real regeneration means a full
  //   dimension-subtype-aware render-to-DXF-primitives pipeline that
  //   doesn't exist in this codebase, materially larger than persisting one
  //   boolean and not specific to MIRRTEXT.
  // - Repurposing DIMENSION's own OCS extrusion sign (the TEXT/MTEXT/
  //   MLEADER mechanism) was ruled out too: `dimensionGeometry.ts` keeps
  //   `textMirrored` and `extrusion` completely independent, and reusing it
  //   risks the same representational ambiguity already found for
  //   MLeader's in-plane case (issue #564).
  // - Landed on this app's OWN app-private XDATA marker instead — the SAME
  //   established pattern this codebase already uses for other app-private,
  //   non-natively-representable per-entity state
  //   (`viewportOverrides.ts`'s "EDIT_DWG_VP_OVERRIDES" marker is the direct
  //   precedent, including by-construction APPID-table registration via
  //   `dxf-io`'s `ensureCustomXDataAppids`). See
  //   `dimensionTextMirroredXData.ts` for the read/write functions and full
  //   format doc — a dedicated "EDIT_DWG_DIM_OVERRIDES" appid, separate from
  //   the pre-existing per-entity DIMVAR-override "DSTYLE" XData below
  //   (`styleOverrides`) so the two never collide.
  //
  // `.optional()`, absent reads as `false` (not mirrored) everywhere —
  // matches the XDATA marker's own presence-only convention (emitted only
  // when `true`, never a literal "0").
  textMirrored: z.boolean().optional(),
  leaderLength: z.number().default(0), // code 40 (radius/diameter)
  anonymousBlockName: z.string().default(""), // code 2
  /* ── S-DIMENSION-EXTRAS — AcDbDimension subclass fields ───────── */
  // All additive-optional so the existing dimension literals built by
  // reducers / tests / AI builders need not be touched; the bridge sets
  // them on import only when the DXF carried a value, and the dxf-io writer
  // emits them on export only when present / non-default.
  version: z.number().int().optional(), // code 280 (AcDbDimension version)
  lineSpacingStyle: z.number().int().optional(), // code 72 (1=AtLeast, 2=Exact)
  lineSpacingFactor: z.number().optional(), // code 41
  insertionPoint: drawingPoint3DSchema.optional(), // code 12/22/32 (clone insert)
  // OCS extrusion normal (210/220/230) + thickness (39). DIMENSION is an OCS
  // planar entity (ezdxf carries both). These ride the COMMON channel in the
  // bridge / dxf-io writer like Line/Circle/Arc — DIMENSION was simply absent
  // from S-EXTRUSION-STORE's entity list and the S-COMMON-EXPORT thickness
  // source, so the value was dropped at this typed-model boundary.
  //
  // thickness is additive-OPTIONAL (read as `?? 0` downstream) rather than
  // `.default(0)` so the ~40 existing `DimensionEntity` literals (reducers,
  // tests, AI builders, parity descriptors) need not be touched — same
  // convention as the base schema's additive handles (S-COMMON-HANDLES) and
  // linetypeScale. The bridge sets it on import only when the DXF carried a
  // non-zero value.
  thickness: z.number().optional(), // code 39
  extrusion: drawingPoint3DSchema.optional(), // code 210/220/230
  // Group C — per-scale annotative context snapshots (DIMANNO parity).
  // Empty when the dimension isn't annotative or only has the implicit
  // 1:1 scale. Each entry mirrors an ACAD_ANNOTATIVE context block
  // attached to the DIMENSION entity in the DXF XData.
  annotativeContexts: z.array(annotativeContextSchema).default([]),
  // Per-entity DIMVAR overrides (DXF XData group codes 1000+). Each
  // field is optional — only stores values that differ from the named
  // style. Replaces the legacy EntityPreview.properties["DIMASZ"]
  // stringified bag; consumers read e.styleOverrides.dimasz instead.
  // 23 DIMVARs mirror DIM_VAR_TABLE in packages/dxf-io/src/dimVarMapping.ts.
  styleOverrides: z
    .object({
      dimasz: z.number().optional(),
      dimtxt: z.number().optional(),
      dimgap: z.number().optional(),
      dimexo: z.number().optional(),
      dimexe: z.number().optional(),
      dimse1: z.boolean().optional(),
      dimse2: z.boolean().optional(),
      dimclrd: z.number().optional(),
      dimclre: z.number().optional(),
      dimdec: z.number().int().optional(),
      dimpost: z.string().optional(),
      // Arrowhead-block overrides (DSTYLE codes 342/343/344). PROVENANCE-SPLIT
      // (SL-11 round-3 #3/#4): `dimblk*` carries an AUTHORED block NAME (a
      // built-in arrow name like `_OPEN30`/`OPEN30`, a user block name, or `""`
      // = default CLOSEDFILLED). `dimblk*Handle` carries an IMPORTED soft-pointer
      // HANDLE — the raw `1005` value `extractDimensionStyleOverrides` read from a
      // source DIMENSION's DSTYLE — so the export knows this is a HANDLE (kept
      // verbatim, remapped, NEVER re-resolved to a same-spelled block name) not a
      // name. The `*Handle` suffix makes each field ride the conformant-skeleton
      // handle-remap machinery (`HANDLE_FIELD_CLASS`), so a reminted arrow
      // BLOCK_RECORD is FOLLOWED. The two are mutually exclusive per code (an
      // entity has EITHER an authored name OR an imported handle).
      dimblk: z.string().optional(),
      dimblk1: z.string().optional(),
      dimblk2: z.string().optional(),
      dimblkHandle: z.string().optional(),
      dimblk1Handle: z.string().optional(),
      dimblk2Handle: z.string().optional(),
      dimtih: z.boolean().optional(),
      dimrnd: z.number().optional(),
      dimlfac: z.number().optional(),
      dimlunit: z.number().int().optional(),
      dimtol: z.boolean().optional(),
      dimlim: z.boolean().optional(),
      dimtp: z.number().optional(),
      dimtm: z.number().optional(),
      dimtfac: z.number().optional(),
    })
    .default({}),
});

/* ── MULTILEADER ──────────────────────────────────── */
export const mleaderBreakPointSchema = z.object({
  // DXF MLEADER break points are 3D: LeaderData breaks 12/22/32 + 13/23/33,
  // LeaderLine breaks 11/21/31 + 12/22/32. optional-z mirrors the DxfPoint3D
  // transport type (DxfMLeaderBreakPoint) so z is carried, not silently
  // dropped; additive — existing bare 2D `{x, y}` literals still parse.
  start: drawingPointOptionalZSchema,
  end: drawingPointOptionalZSchema,
  breakIndex: z.number().int().optional(), // group 90 — LeaderLine breaks set it; LeaderData breaks omit it
});

export const mleaderLeaderLineSchema = z.object({
  // CONTEXT_DATA LeaderLine vertices (group 10/20/30). optional-z mirrors the
  // DxfPoint3D transport (DxfMLeaderLeaderLine.vertices) so the vertex z is
  // carried, not silently dropped — matching the sibling breakPoints /
  // lastLeaderPoint / doglegVector 3D upgrade (FIX 3). Additive: existing bare
  // 2D `{x, y}` literals still parse; the bridge maps via toDrawingPoint3D
  // (import) / toDxfPoint3D (export, z ?? 0).
  vertices: z.array(drawingPointOptionalZSchema).min(1),
  // CONTEXT_DATA LeaderLine breaks — group 90 break_index + 11/21/31 start +
  // 12/22/32 end (ezdxf LeaderLine.breaks). Distinct flavour from the LeaderData
  // breaks (alternating 12↔13, no index) carried on the node below.
  breakPoints: z.array(mleaderBreakPointSchema).default([]),
  arrowheadBlock: z.string().default(""),

  // ── S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed LEADER_LINE fields
  // (CONTEXT_DATA depth 3, ezdxf LeaderLine). Per the §7 code map; populated by
  // sub-slice B (parser) / projected by C (bridge). All optional/additive.
  leaderLineIndex: z.number().int().optional(), // 91 — LeaderLine.index
  colorOverride: z.string().optional(), // 92 — per-line color override
  lineTypeHandle: z.string().optional(), // 340 — line-type handle (R2010)
  lineType: z.number().int().optional(), // 170 — line-type id
  lineWeightOverride: z.string().optional(), // 171 — per-line lineweight override
  // Codex #268 — in-frame verbatim remainder for THIS LEADER_LINE (CONTEXT_DATA
  // 304…305). Any group the parser could not promote to a typed field, in source
  // order, so the writer re-emits it INSIDE the leader-line frame rather than flat
  // at the end of CONTEXT_DATA (DXF structural-frame fidelity). Same {code,value}
  // pair shape as the entity-level rawContextGroups; optional/additive so existing
  // leader-line literals keep typechecking. Threads through the bridge via the
  // leader-root spreads (...lineRest) in both directions.
  rawGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),
});

export const mleaderLeaderNodeSchema = z.object({
  doglegVector: drawingPoint3DSchema.optional(), // 11/21/31 (iff hasDoglegVector)
  lines: z.array(mleaderLeaderLineSchema).default([]),
  doglegLength: z.number().optional(), // 40 — LeaderData.dogleg_length

  // ── S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed LEADER fields
  // (CONTEXT_DATA depth 2, ezdxf LeaderData). All optional/additive.
  lastLeaderPoint: drawingPoint3DSchema.optional(), // 10/20/30 (iff hasLastLeaderLine)
  hasLastLeaderLine: z.boolean().optional(), // 290 — last_leader_point present
  hasDoglegVector: z.boolean().optional(), // 291 — dogleg_vector present
  leaderIndex: z.number().int().optional(), // 90 — LeaderData.index
  attachmentDirection: z.number().int().optional(), // 271 — attachment_direction (R2010)
  // LeaderData breaks — alternating 12↔13 (start↔end) point pairs, NO index
  // (contrast LeaderLine breaks above). Reuses the shared start/end pair.
  // Optional (not .default([])) so existing node literals that omit it keep
  // typechecking — additive, no consumer break.
  breakPoints: z.array(mleaderBreakPointSchema).optional(),
  // Codex #268 — in-frame verbatim remainder for THIS LEADER (CONTEXT_DATA
  // 302…303) but OUTSIDE its LEADER_LINE sub-frames. Same rationale / shape as
  // the leader-line rawGroups above; threads through the bridge via ...nodeRest.
  rawGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),
});

export const mleaderLeaderRootSchema = z.object({
  nodes: z.array(mleaderLeaderNodeSchema).min(1),
});

// Block attribute reference (B1 — AutoCAD DXF Reference group sequence
// 330 / 177 / 44 / 302). One entry per ATTDEF inside the content block.
// See atlight.github.io DXF leader notes: common group code 330
// references handle of relevant ATTDEF, followed by index (177),
// width (44), and attribute value (302).
export const mleaderBlockAttributeRefSchema = z.object({
  attDefHandle: z.string().min(1), // group 330 — soft pointer to ATTDEF
  index: z.number().int().default(0), // group 177 — attribute index
  width: z.number().default(0), // group 44 — attribute width
  value: z.string().default(""), // group 302 — attribute value
});

// MLEADER reuses the common base but keeps its long-standing defaults
// for the MLEADER-specific fields so existing round-trip tests still
// pass (`mleaderContextDataSchema.parse({})` must yield textColor
// "ByLayer", doglegLength 8, …). Downstream consumers that only need
// the shared fields (Dimension) can use `annotativeContextSchema`.
export const mleaderContextDataSchema = z.object({
  scale: z.number().default(1),
  scaleName: z.string().default(""),
  textHeight: z.number().default(2.5),
  arrowheadSize: z.number().default(2.5),
  doglegLength: z.number().default(8),
  landingGap: z.number().default(2),
  textRotation: z.number().default(0),
  textColor: z.string().default("ByLayer"),
  textWidth: z.number().default(0),
});

// ── S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — typed CONTEXT_DATA model ──
//
// The AcDbMLeader entity embeds a single `CONTEXT_DATA` record (ezdxf
// MLeaderContext) that AutoCAD round-trips byte-for-byte. Prior to this slice
// the bridge recovered only leader-line vertices + node dogleg and dropped the
// context header, MTextData, BlockData, plane, attach + LeaderData/LeaderLine
// metadata. This adds the typed nested model per the §7-gated, ezdxf-verified
// field↔code map (`tmp_smleader_codemap.md`). Schema/types ONLY — the parser
// (sub-slice B), bridge (C) and writer (D) wire it up. Every code not yet
// promoted to a typed field is preserved verbatim in `rawContextGroups` on the
// entity (byte-faithfulness guarantee, ПРИОРИТЕТ #1).
//
// NOTE: distinct from `mleaderContextDataSchema` above (the per-annotative-scale
// snapshot, B2). This is the singular structural CONTEXT_DATA block.

// SCOPE 2 — MTextData (inline iff context has_mtext=290; NO own brace). ezdxf
// MTextData, emit order per code map. Colors stay strings to match the rest of
// the MLEADER model (textColor/leaderLineColor/blockColor); the bridge maps the
// raw ACI/true-color int.
//
// BYTE-FAITHFULNESS (Codex #270): every field is `.optional()` — NOT
// `.default(...)`. A `.default()` MATERIALIZES the value on a sparse imported
// CONTEXT_DATA (a source that omitted, say, group 43/44/90/291), and the
// byte-faithful writer (`emitMTextData`, `s.opt`/`s.bool`) then re-emits the
// invented `43\n0`, `90\nByBlock`, `291\n0`, … — inventing groups the source
// never had. Optional preserves source absence so a parse → write round-trip
// re-emits EXACTLY the present fields (ПРИОРИТЕТ #1; never infer).
export const mleaderMTextDataSchema = z.object({
  defaultContent: z.string().optional(), // 304 — MTEXT contents (dual-use code)
  extrusion: drawingPoint3DSchema.optional(), // 11/21/31
  styleHandle: z.string().optional(), // 340 — TextStyle handle (#243)
  insert: drawingPoint3DSchema.optional(), // 12/22/32 — insertion point
  textDirection: drawingPoint3DSchema.optional(), // 13/23/33 — text direction
  rotation: z.number().optional(), // 42
  width: z.number().optional(), // 43
  definedHeight: z.number().optional(), // 44 — defined_height (#256 F2)
  lineSpacingFactor: z.number().optional(), // 45
  lineSpacingStyle: z.number().int().optional(), // 170
  color: z.string().optional(), // 90
  alignment: z.number().int().optional(), // 171 — attachment alignment (#258)
  flowDirection: z.number().int().optional(), // 172
  backgroundColor: z.string().optional(), // 91 — bg_color
  backgroundScaleFactor: z.number().optional(), // 141 — bg_scale
  backgroundTransparency: z.number().optional(), // 92 — bg_transparency
  useWindowBackground: z.boolean().optional(), // 291 — use_window_bg
  hasBackgroundFill: z.boolean().optional(), // 292 — has_bg_fill
  columnType: z.number().int().optional(), // 173
  useAutoHeight: z.boolean().optional(), // 293 — use_auto_height
  columnWidth: z.number().optional(), // 142
  columnGutter: z.number().optional(), // 143
  columnFlowReversed: z.boolean().optional(), // 294
  columnSizes: z.array(z.number()).optional(), // 144 — repeat per column
  useWordBreak: z.boolean().optional(), // 295
});

// SCOPE 3 — BlockData (inline iff context has_block=296; NO own brace). ezdxf
// BlockData, emit order per code map.
// BYTE-FAITHFULNESS (Codex #270): all-optional, never `.default()` — same
// rationale as mleaderMTextDataSchema above (a default materializes + the writer
// re-emits invented `46\n0` / `93\nByBlock` / matrix groups on a sparse source).
export const mleaderBlockDataSchema = z.object({
  blockRecordHandle: z.string().optional(), // 341 — BLOCK_RECORD handle
  extrusion: drawingPoint3DSchema.optional(), // 14/24/34
  insert: drawingPoint3DSchema.optional(), // 15/25/35
  scale: drawingPoint3DSchema.optional(), // 16/26/36 — scale vector
  rotation: z.number().optional(), // 46
  color: z.string().optional(), // 93
  // 47 ×16 — block transform matrix (repeating group 47). Stored row-major as a
  // flat 16-number array; length not constrained.
  matrix: z.array(z.number()).optional(),
});

// SCOPE 1 — MLeaderContext header (300{…301}, depth 1). ezdxf emit order.
// BYTE-FAITHFULNESS (#294 r3, primary arm of the r2 no-defaults fix): every
// field is `.optional()` — NOT `.default(...)`. The r2 fix made the SECONDARY
// header absence-preserving but left the primary defaults in place, so a
// SPARSE primary CONTEXT_DATA (e.g. carrying only an explicit `290 0` or
// `296 0`) MATERIALIZED the defaulted siblings on import (`scale: 1`,
// `hasBlock: false`, `planeNormalReversed: false`) and the byte-faithful
// writer (`emitContextHeader`) re-emitted invented `40 1` / `296 0` / `297 0`
// groups the source never had. Optional preserves source absence so an
// explicit-zero sparse primary round-trips byte-stable (ПРИОРИТЕТ #1; same
// rationale as mleaderMTextDataSchema / mleaderBlockDataSchema, Codex #270).
export const mleaderContextSchema = z.object({
  scale: z.number().optional(), // 40 — context scale
  // 10/20/30 — content base point (NOT 110/120/130 = planeOrigin; corrects the
  // prior mis-spec, code map disputed-resolution #1).
  basePoint: drawingPoint3DSchema.optional(),
  charHeight: z.number().optional(), // 41 — char_height
  arrowHeadSize: z.number().optional(), // 140 — arrow_head_size
  landingGapSize: z.number().optional(), // 145 — landing_gap_size
  textLeftAttachment: z.number().int().optional(), // 174 — left_attach
  textRightAttachment: z.number().int().optional(), // 175 — right_attach
  textAlignType: z.number().int().optional(), // 176 — text_align_type
  // 177 — attachment_type (context). NOT block connection-type (that is BODY
  // 176; code map disputed-resolution #2). The body-level blockConnectionType
  // field stays on the entity (sub-slice C).
  attachmentType: z.number().int().optional(),
  hasMText: z.boolean().optional(), // 290 — MTextData inline present
  hasBlock: z.boolean().optional(), // 296 — BlockData inline present
  mTextData: mleaderMTextDataSchema.optional(), // present iff hasMText
  blockData: mleaderBlockDataSchema.optional(), // present iff hasBlock
  planeOrigin: drawingPoint3DSchema.optional(), // 110/120/130
  planeXAxis: drawingPoint3DSchema.optional(), // 111/121/131
  planeYAxis: drawingPoint3DSchema.optional(), // 112/122/132
  planeNormalReversed: z.boolean().optional(), // 297
  topAttachment: z.number().int().optional(), // 272 — top_attach (R2010)
  bottomAttachment: z.number().int().optional(), // 273 — bottom_attach (R2010)
});

// #294 Codex P2 — one annotative SECONDARY CONTEXT_DATA block (every
// CONTEXT_DATA after the first; one per scale). The dxf-io transport keeps
// these fully TYPED (`secondaryContexts`: context header + leader hierarchy +
// per-block verbatim raw remainder — Codex #268/#285-9), but the model had no
// home for them, so the app path (importDxfStringToDrawingDocument →
// exportDrawingDocumentToDxfString) dropped every secondary block — and with
// the #285-9 per-block raw-group fix, a secondary-only raw group went from
// "preserved but leaked to primary" to "lost entirely". Mirrors the transport
// shape with the model's own typed sub-schemas (no flattening, ПРИОРИТЕТ #1).
export const mleaderSecondaryContextSchema = z.object({
  // Full typed CONTEXT_DATA header (+ INLINE MTextData / BlockData) of this
  // secondary block. #294 r2 made the secondary header absence-preserving (a
  // dedicated no-defaults extend); #294 r3 removed the primary schema's
  // `.default(...)`s too (the SPARSE-primary arm of the same invented-groups
  // bug), so primary and secondary headers share ONE schema again.
  context: mleaderContextSchema,
  // This block's OWN leader hierarchy. `.default([])` (not `.min(1)`): a
  // secondary block may legitimately carry no LEADER frames.
  leaderRoots: z.array(mleaderLeaderRootSchema).default([]),
  // Unrecognised groups inside THIS secondary block's header (or a dropped
  // leader/leader-line frame it owns), verbatim in source order, so the writer
  // re-emits them in THIS block — not the primary. Same {code,value} shape as
  // the entity-level rawContextGroups.
  rawContextGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),
});

export const multileaderEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Multileader"),
  styleName: z.string().default("Standard"),
  // SL-5 P2 — AcDbMLeader body group 340 = style_handle (the MLEADERSTYLE record
  // handle). Round-trips by NAME on `styleName`; AutoCAD persists it as a HANDLE.
  // Imported: the raw source handle is carried verbatim (source-preserve). Export:
  // resolved from styleName by the document orchestrator (stampMLeaderLeaderStyle
  // Handles) — same split as textStyleName↔textStyleHandle. Optional/additive.
  leaderStyleHandle: z.string().optional(),
  // SL-5 P3a — AcDbMLeader body group 270 = version (ezdxf default 2). Optional so
  // a source-omitted 270 stays absent on the model; the export writer defaults to
  // 2 (matching ezdxf's unconditional write).
  version: z.number().int().optional(),

  // Structure — hierarchical. #432 r13 (@codex R13-3) — `.default([])`, NOT
  // `.min(1)`: a CONTENT-ONLY (leaderless) multileader is legitimate in AutoCAD /
  // ezdxf (the leader lines were deleted, leaving just the MTEXT/block content —
  // ezdxf `MLeaderContext.export_dxf` emits ZERO `302 LEADER{` frames for it). The
  // `.min(1)` rejected a valid ezdxf-authored leaderless MLEADER on import
  // validation; R11-1 already keeps `leaderRoots=[]` at the dxf-io layer (no
  // phantom origin leader), so the model must accept it too.
  leaderRoots: z.array(mleaderLeaderRootSchema).default([]),

  // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — the single typed CONTEXT_DATA
  // record (ezdxf MLeaderContext). Optional/additive: populated by sub-slice B
  // (parser) and projected by C (bridge); consumers must not assume presence.
  contextData: mleaderContextSchema.optional(),

  // Text content
  contentType: z.number().int().default(2), // 1=Block, 2=MText
  textContents: z.string().default(""),
  textStyleName: z.string().default("Standard"),
  textHeight: z.number().default(2.5),
  textRotation: z.number().default(0),
  // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — the MTEXT
  // CONTENT's handedness companion to `textRotation`, mirroring how a real
  // MTEXT entity's `extrusion` (0,0,−1) marks a mirrored glyph run distinct
  // from a plain rotation (see `geometryTransforms.ts`'s "MTEXT HANDEDNESS"
  // doc). Its real DXF home is the SAME CONTEXT_DATA MTextData this entity's
  // flat `textRotation` flattens (`mleaderMTextDataSchema.extrusion`, group
  // 11/21/31) — this flat field is the render/edit-surface convenience twin,
  // hydrated from / projected into that vector by the bridge exactly like
  // `textRotation` already is (`dxfBridge.drawingToDxf.ts` /
  // `dxfBridge.entityConversion.ts modelMLeaderContextToDxf` /
  // `buildAuthoredMLeaderContext`). `.optional()` (NOT `.default()`), same
  // convention as `thickness`/other additive fields elsewhere in this file —
  // absent reads as `false` (not mirrored, a plain (0,0,1) extrusion, no
  // group emitted) at every read site, so the ~60 existing `MultileaderEntity`
  // literals (reducers, tests, AI builders) need not be touched.
  textMirrored: z.boolean().optional(),
  textColor: z.string().default("ByLayer"),
  textAttachmentType: z.number().int().default(1),
  textAttachmentDirection: z.number().int().default(0),
  textFrameDisplay: z.boolean().default(false),
  textLineSpacingStyle: z.number().int().default(1),
  textLineSpacingFactor: z.number().default(1),
  textLineSpacingDistance: z.number().default(0),
  textWidth: z.number().default(0),

  // Leader properties
  arrowheadSize: z.number().default(2.5),
  arrowheadBlock: z.string().default(""),
  leaderLineType: z.number().int().default(1),
  leaderLineColor: z.string().default("ByBlock"),
  leaderLineWeight: z.string().default("ByBlock"),
  landingEnabled: z.boolean().default(true),
  doglegEnabled: z.boolean().default(true),
  doglegLength: z.number().default(8),
  landingGap: z.number().default(2),
  breakSize: z.number().default(3.75),

  // Block content
  blockName: z.string().default(""),
  // AcDbMLeader BODY group 344 = block_record_handle (raw handle). Carried
  // alongside the resolved `blockName` (same split as textStyleHandle vs
  // textStyleName) so the writer re-emits 344 verbatim while rendering uses the
  // resolved name. Optional/additive (Codex #271).
  blockRecordHandle: z.string().optional(),
  blockScale: z.number().default(1),
  blockRotation: z.number().default(0),
  // #432 r11 (@codex seam) — AcDbMLeader BODY block_scale_vector (group 10/20/30,
  // ezdxf `write_vertex(10, block_scale_vector)`). OPTIONAL (source-preserving):
  // present only when the source carried the body 10/20/30, so an omitted vector
  // stays omitted end-to-end (parser→model→export→writer). The pure dxf-io
  // round-trip already preserves it; this carries it through the DrawingModel
  // (app) save path too.
  blockScaleVector: drawingPoint3DSchema.optional(),
  blockColor: z.string().default("ByBlock"),
  // AcDbMLeader BODY group 176 (block_connection_type). OPTIONAL — NOT
  // `.default(0)` — so a source that OMITTED body 176 preserves that absence
  // end-to-end (Codex #270 regression fix). A `.default(0)` MATERIALIZES 0 on a
  // source-omitted 176, and the byte-faithful dxf-io writer (`g.blockConnection
  // Type !== undefined`) then re-emits an invented `176\n0` every round-trip.
  // Mirrors the sibling optional body fields (blockRecordHandle) that preserve
  // source absence; the bridge import sets it only when the transport carried it.
  blockConnectionType: z.number().int().optional(),
  // B1 — ATTDEF/ATTRIB linkage for block content. Parser reads the
  // (330, 177, 44, 302) repeating group and accumulates entries here.
  blockAttributeReferences: z.array(mleaderBlockAttributeRefSchema).default([]),

  // B2 — annotative contexts (array of per-scale snapshots). Empty
  // array when the multileader is not annotative or only has the
  // implicit 1:1 scale.
  annotativeContexts: z.array(mleaderContextDataSchema).default([]),

  // Location and meta
  textLocation: drawingPointSchema,
  // Content base point (CONTEXT_DATA 10/20/30 — corrects the prior mis-spec
  // that conflated it with 110/120/130; that is the plane origin, now carried as
  // contextData.planeOrigin). code map disputed-resolution #1.
  basePoint: drawingPointSchema.optional(),
  // AcDbMLeader BODY group 90 (ezdxf Scope-6 property_override_flags). gap-C.2:
  // corrected from the prior body-175 source code.
  propertyOverrideFlags: z.number().int().default(0),
  // AcDbMLeader BODY group 175 (ezdxf Scope-6 text_alignment_type). gap-C.2 —
  // 175 was previously mis-read as propertyOverrideFlags (override flags are
  // body 90); this typed field is its correct home.
  textAlignmentType: z.number().int().default(0),

  // PR-14 — entity-level "is annotative" is no longer a stored boolean
  // flag. AutoCAD parity: an MLEADER is annotative when it has at least
  // one entry in annotativeContexts[] (the per-scale snapshot array
  // populated by ANNOAUTOSCALE / OBJECTSCALE workflows). Derive via
  // `entity.annotativeContexts.length > 0` at every consumer.

  // Round-trip escape hatch for unknown MLEADER group codes.
  extraMlVars: z.record(z.string(), z.string()).default({}),

  // S-MLEADER-CONTEXT-STRUCTURAL (sub-slice A) — verbatim CONTEXT_DATA fallback
  // channel. Every context group code not yet promoted to a typed field above is
  // preserved here in original emit order (byte-faithfulness guarantee,
  // ПРИОРИТЕТ #1). Same {code,value} pair shape as `extraGroups`, and optional
  // like it so existing Multileader literals that omit it keep typechecking —
  // additive, no consumer break.
  rawContextGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),

  // #294 Codex P2 — annotative SECONDARY CONTEXT_DATA blocks (every block
  // after the first), fully typed per block (see mleaderSecondaryContext
  // Schema). Optional — NOT `.default([])` — so a single-context MLEADER
  // preserves source absence end-to-end (absence-preserving, like the sibling
  // rawContextGroups above); the bridge sets it only when the transport
  // carried secondary blocks.
  secondaryContexts: z.array(mleaderSecondaryContextSchema).optional(),
});

/* ── REVISION CLOUD ───────────────────────────────── */
export const revisionCloudEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Revision Cloud"),
  vertices: z.array(polylineVertexSchema).min(3),
  closed: z.boolean().default(true),
  // DXF group 70 bit 128 — plinegen (continuous linetype generation). A
  // revision cloud is stored on disk as an LWPOLYLINE, so it carries the same
  // 70 flag bits as an LWPOLYLINE; bit 128 lives alongside the closed bit (1)
  // and must round-trip or an imported cloud loses plinegen on save (@codex
  // #422 P2). Mirrors lwPolylineEntitySchema.plinegen; absent ⇒ false.
  plinegen: z.boolean().optional(),
  arcChordLength: z.number().default(15),
  approximateArcLength: z.number().default(15),
  revisionCloudStyle: z.string().default("Normal"),
  creationMode: z
    .enum(["Rectangular", "Polygonal", "Object"])
    .default("Rectangular"),
  // REVCLOUD is stored on disk as an LWPOLYLINE-derived entity, so it carries
  // the same polyline scalars (S-REVCLOUD-FIELDS, audit §12.1):
  //   - constantWidth (DXF group 43) — uniform segment width
  //   - elevation     (DXF group 38) — Z elevation of the polyline plane
  // These were read/written by dxf-io but silently DROPPED at the bridge
  // (parsed-then-unmapped); they now round-trip onto the typed model.
  constantWidth: z.number().default(0),
  elevation: z.number().default(0),
  // DXF group 39 — extrusion thickness (extruded 2D cloud). Routed through the
  // COMMON channel (common.thickness) like Line/Circle/Arc, not a per-geometry
  // code. AutoCAD default 0.
  thickness: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS normal). Optional store
  // (REVCLOUD joins S-EXTRUSION-STORE via the COMMON channel); absent ⇒
  // (0,0,1) default. OCS→WCS render is DEFUSE-owned — this slice only
  // round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
});

/* ── WIPEOUT ──────────────────────────────────────── */
// AutoCAD WIPEOUT entity (AcDbWipeout, extends AcDbRasterImage). For
// our editor we use a simplified model: a closed polygon boundary
// with opaque background fill that hides entities rendered beneath
// it. The frame flag mirrors AutoCAD's WIPEOUT frame visibility
// toggle (system variable WIPEOUTFRAME / per-entity 280 bit).
//
// AutoCAD encodes WIPEOUT in DXF as a raster-image-derived entity:
//   - Subclass markers AcDbEntity / AcDbRasterImage / AcDbWipeout
//   - Group 71 (image-data class) = 1 (always for wipeout)
//   - Group 91 — clip vertex count (boundary)
//   - Repeating 14/24 — clip-boundary vertices
//   - Group 280 — frame display (0 = hidden, 1 = shown)
//
// We persist vertices as world-space 2D points; the DXF writer
// converts to/from the boundary vertex format that AutoCAD expects.
// First-pass implementation supports rectangular WIPEOUT (4
// vertices); polygon-pick mode is deferred.
export const wipeoutEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Wipeout"),
  vertices: z.array(drawingPointSchema).min(3),
  frame: z.boolean().default(true),
  // Clip mode — DXF group 290 (AcDbRasterImage, ezdxf image.py `clip_mode`,
  // validator is_integer_bool, default 0): 0 = mask OUTSIDE the boundary
  // (AutoCAD default), 1 = mask INSIDE. Source-preserved verbatim on round-trip
  // (a source `290 1` must NOT flip to outside on import→export).
  clipMode: z.union([z.literal(0), z.literal(1)]).default(0),
});

/* ── HATCH ────────────────────────────────────────── */
export const hatchBoundaryEdgeSchema = z.discriminatedUnion("edgeType", [
  z.object({
    edgeType: z.literal("line"),
    start: drawingPointSchema,
    end: drawingPointSchema,
  }),
  z.object({
    edgeType: z.literal("arc"),
    center: drawingPointSchema,
    radius: z.number(),
    startAngle: z.number(),
    endAngle: z.number(),
    counterclockwise: z.boolean().default(false),
  }),
  z.object({
    edgeType: z.literal("ellipticArc"),
    center: drawingPointSchema,
    majorAxisEnd: drawingPointSchema,
    minorRatio: z.number(),
    startAngle: z.number(),
    endAngle: z.number(),
    counterclockwise: z.boolean().default(false),
  }),
]);

export const hatchBoundaryPathSchema = z.object({
  pathTypeFlag: z.number().int().default(1),
  edges: z.array(hatchBoundaryEdgeSchema).min(1),
  // ── Associative source-boundary refs (S-HATCH-ASSOC-REFS) ──
  // For an ASSOCIATIVE hatch (group 71 = 1) AutoCAD stores, per boundary
  // path, the soft-pointer handles (group 330) of the source entities that
  // define this path's geometry — emitted right after the path's source
  // object count (group 97 > 0). We round-trip them verbatim as a per-path
  // string array so re-saving an associative hatch does not silently drop
  // its boundary references (CLAUDE.md ПРИОРИТЕТ #1). Live re-association
  // (recomputing geometry when the source moves) is a behaviour subsystem
  // and is OUT OF SCOPE here — handle preservation only. Absent for a
  // non-associative path; an empty array when the path declared 0 sources.
  // Source of truth: DXF reference (AcDbHatch boundary 330) + ezdxf
  // `BoundaryPathData.source_boundary_objects`.
  sourceBoundaryHandles: z.array(z.string()).optional(),
});

// ── Gradient fill (AcDbHatch gradient extension) ─────
// A gradient hatch sets DXF group 450=1 and carries the full gradient
// definition AutoCAD writes. We preserve EVERY group code (incl. the
// reserved 451 and the as-written numberOfColors 453) so the DXF
// round-trip is value-faithful — никаких boolean-упрощений (CLAUDE.md
// ПРИОРИТЕТ #1). Source of truth: DXF reference (HATCH gradient codes)
// + ezdxf `ezdxf.entities.Gradient` / `GradientData`.
//
// Each color stop is a (463 position, 63 ACI, 421 true-color) triple.
// `aci` (group 63) and `trueColor` (group 421, 0xRRGGBB) are EACH omitted only
// when AutoCAD did not write that code for the stop, so the absence survives
// round-trip exactly — real AutoCAD gradients sometimes carry only 421 (no 63),
// and injecting a `63 0` we never read would break byte-fidelity.
export const hatchGradientColorSchema = z.object({
  position: z.number(), // 463 stop value (0.0 / 1.0)
  aci: z.number().int().optional(), // 63 — omit when absent
  trueColor: z.number().int().optional(), // 421 (0xRRGGBB) — omit when absent
});
export const hatchGradientSchema = z.object({
  kind: z.number().int().default(1), // 450 (1 = gradient)
  reserved: z.number().int().default(0), // 451 (reserved — preserve)
  oneColor: z.number().int().default(0), // 452 (0=two-color,1=single tint)
  numberOfColors: z.number().int().default(2), // 453 (preserve as written)
  rotation: z.number().default(0), // 460 radians
  shift: z.number().default(0), // 461 0..1
  tint: z.number().default(0), // 462 0..1
  name: z.string().default("LINEAR"), // 470
  colors: z.array(hatchGradientColorSchema).default([]),
});

// ── Custom pattern-definition line (S-HATCH-CUSTOM) ──
// A non-predefined ("custom") HATCH carries one pattern-definition line per
// hatch line family (DXF group 78 = the count). Each line is the full PAT
// family AutoCAD/ezdxf model — никаких opaque blob/string упрощений
// (CLAUDE.md ПРИОРИТЕТ #1). Source of truth: DXF reference (AcDbHatch
// "Pattern data") + ezdxf `ezdxf.entities.Hatch.set_pattern_definition` /
// `PatternLine` ([angle, base, offset, dash_length_items]).
//
//   angle       — group 53 (pattern line angle, degrees)
//   base        — groups 43 (x) / 44 (y) (pattern line base point)
//   offset      — groups 45 (x) / 46 (y) (pattern line offset — the
//                 displacement between successive dash rows; STRUCTURED
//                 {x,y}, never flattened, per audit §12.1 offset-vector row)
//   dashLengths — group 49, repeated `count` times (group 79 = count).
//                 Positive = dash, negative = gap, 0 = dot (DXF convention).
//                 An empty array = a solid (unbroken) pattern line.
export const hatchPatternLineSchema = z.object({
  angle: z.number(), // 53
  base: drawingPointSchema, // 43 / 44
  offset: drawingPointSchema, // 45 / 46 — structured, NOT flattened
  dashLengths: z.array(z.number()).default([]), // 49 (count = 79)
});

export const hatchEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Hatch"),
  patternName: z.string().default("ANSI31"),
  solidFill: z.boolean().default(false),
  associative: z.boolean().default(true),
  hatchStyle: z.number().int().default(0),
  patternType: z.number().int().default(1),
  patternAngle: z.number().default(0),
  patternScale: z.number().default(1),
  doubleLine: z.boolean().default(false),
  // ── Custom pattern-definition lines (S-HATCH-CUSTOM) ──
  // DXF groups 78 (count) + per-line 53/43/44/45/46/79/49. Present ONLY for
  // custom (non-predefined, non-solid) hatch patterns; absent for solid fills
  // and predefined patterns (which carry only the pattern NAME, group 2). The
  // pattern NAME (`patternName`) and these defining lines are independent
  // channels — a custom pattern survives round-trip via these typed lines, not
  // a re-derived name. Omitted (undefined) when the source carried no 78 block.
  customPatternLines: z.array(hatchPatternLineSchema).optional(),
  // Real-world DXF from AutoCAD can carry HATCH entities with zero
  // boundary paths (degenerate hatch — visually invisible but legal,
  // and round-trip preservation per CLAUDE.md "ничего не упрощать").
  // Empty array accepted; structure of any path still validated.
  boundaryPaths: z.array(hatchBoundaryPathSchema),
  // ── Elevation point (S-HATCH-ELEVATION-XY) ──
  // AcDbHatch carries a full WCS elevation POINT (groups 10/20/30). The Z
  // component (group 30) is the scalar `elevation` below — kept a `number`
  // (not promoted to a 3D point) so the pervasive `elevation ?? 0` reads in
  // the compact entity store, command-engine geometry types and render
  // adapters stay valid (low-blast-radius choice per the audit slice). The
  // X/Y components (groups 10/20, usually 0) are preserved here so the full
  // elevation point round-trips. Omitted when 0 so default hatches stay
  // byte-compact. Source: DXF reference (HATCH 10/20/30) + ezdxf
  // `Hatch.dxf.elevation` (a Vec3).
  elevationX: z.number().optional(),
  elevationY: z.number().optional(),
  elevation: z.number().default(0),
  // ── Pixel size (S-HATCH-PIXELSIZE) ──
  // DXF group 47 — the pixel size used to determine the density of the
  // SOLID/gradient fill intersection sampling. Parsed by dxf-io but
  // previously dropped on the way to the typed model; preserved verbatim so
  // it round-trips. Absent when the source carried no 47.
  pixelSize: z.number().optional(),
  // ── Extrusion / OCS normal (S-HATCH-EXTRUSION) ──
  // DXF groups 210/220/230. HATCH is an OCS-planar entity; AutoCAD/ezdxf
  // carry an extrusion normal. Stored here so the normal round-trips
  // (store-first per the audit master C2). The OCS→WCS render lift is
  // DEFUSE-owned (edge-14 / S10b-EXT) and OUT OF SCOPE here. Optional;
  // absent ⇒ the identity (0,0,1) normal.
  extrusion: drawingPoint3DSchema.optional(),
  seedPoints: z.array(drawingPointSchema).default([]),
  // ── Annotative (PR-1) ──
  // HATCH context (`AcDbHatchScaleContextData`) carries pattern scale
  // and angle per cannoscale — boundary geometry stays in world coords.
  // Empty for non-annotative hatches.
  annotativeContexts: z.array(annotScaleContextSchema).default([]),
  creationScaleName: z.string().optional(),
  // ── Gradient fill (PDF-6.5a) ──
  // Present ONLY for gradient hatches (DXF group 450=1). Absent for
  // solid/pattern hatches so their DXF stays byte-identical. A gradient
  // hatch in DXF also sets solidFill=1 — `solidFill` is preserved
  // independently and is NOT a substitute for this structured field.
  gradient: hatchGradientSchema.optional(),
});

/* ── BLOCK (INSERT reference) ─────────────────────── */

// One ATTRIB attached to an INSERT. `tag` is inherited from the
// matching ATTDEF inside the block definition; `value` is the current
// user-provided attribute value. Override fields mirror AutoCAD's
// EATTEDIT dialog — a user can override the text height, rotation,
// position and style of an individual ATTRIB without re-authoring the
// block. When the override is undefined we inherit from the ATTDEF.
// `rawGroups` round-trips vendor-specific or otherwise unknown DXF
// groups from the source ATTRIB so nothing is lost when we re-emit.
export const blockInsertAttributeSchema = z.object({
  tag: z.string(),
  value: z.string(),
  handle: z.string().default(""),
  overrideHeight: z.number().optional(),
  overrideRotation: z.number().optional(),
  // GitHub Codex review PR #558 (post-rebase fresh pass) P2 — DXF group 71
  // (text generation flags), the SAME real ATTRIB/AcDbText field TEXT's own
  // `generationFlags` carries (`textEntitySchema.generationFlags`,
  // TEXT_GEN_FLAG_BACKWARD=2 / TEXT_GEN_FLAG_UPSIDE_DOWN=4 in
  // `command-engine/geometryTransforms.ts`) — the handedness companion to
  // `overrideRotation` for a WORLD-placed attribute. The raw DXF parser
  // ALREADY reads this off every ATTRIB (`ATTDEF_GROUP_READERS`'s shared 71
  // reader), but the import bridge previously dropped it when materialising
  // `BlockInsertAttribute` — a genuine pre-existing gap, not a NEW DXF
  // concept. `.optional()`, matching `overrideRotation`'s own convention —
  // absent means "no override", reading as `0` (no flip) at every render/
  // export site, same as TEXT's own group 71 default.
  overrideGenerationFlags: z.number().int().optional(),
  // INVARIANT — `overridePosition` is a WORLD/absolute placed position (the
  // resolved DXF group 10/20), NOT a block-local point. When SET it is the
  // final position the ATTRIB renders / exports at; every producer must write
  // a world point (import from the raw ATTRIB insertion point; EATTEDIT / AI
  // from the world point the user picked) and every consumer must treat it as
  // absolute — the export encoder (`dxfBridge.attribExportPosition.ts
  // resolveAttribExportPosition`) returns it verbatim, and the scene3d render
  // (`attdefFromEntity.ts resolvePlacement`) places the text there WITHOUT
  // re-applying the parent INSERT affine. When UNSET the ATTRIB follows its
  // ATTDEF template LIVE in block-local space (rides the INSERT transform), so
  // the authoring paths (`command-engine` create + value-only DDATTE) leave it
  // undefined rather than baking a block-local point that would be
  // mis-interpreted as world. (Pre-existing exception tracked separately: the
  // PDF plot (`print/pdf/converters/insertConverter.ts attributeToTextRun`,
  // lines ~790-792) still transforms a set value as if block-local; the EXPLODE
  // reducers were corrected to honour this invariant in #470 via the shared
  // `command-engine/attribExplodePlacement.ts` resolver.)
  overridePosition: drawingPointSchema.optional(),
  overrideStyle: z.string().optional(),
  invisible: z.boolean().optional(),
  rawGroups: z.unknown().optional(),
});

export const blockInsertEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Block"),
  blockName: z.string(),
  // DXF INSERT carries a 3D insertion point (group 10/20/30). z is optional
  // in the model (drawingPointOptionalZSchema) so the 2D `{x, y}` literals
  // built by reducers / compact store / tests keep typechecking; the bridge
  // preserves a real z across the round-trip via toDrawingPoint3D (import)
  // / toDxfPoint3D (export, z ?? 0) — DEFUSE S-INSERT-INSERTPT-Z.
  insertionPoint: drawingPointOptionalZSchema,
  scaleX: z.number().default(1),
  scaleY: z.number().default(1),
  scaleZ: z.number().default(1),
  rotation: z.number().default(0),
  columnCount: z.number().int().default(1),
  rowCount: z.number().int().default(1),
  columnSpacing: z.number().default(0),
  rowSpacing: z.number().default(0),
  // DXF group 210/220/230 — extrusion direction (OCS). Defaults to
  // (0,0,1) meaning no OCS transform.
  extrusion: drawingPoint3DSchema.default({ x: 0, y: 0, z: 1 }),
  // ATTRIB children captured during DXF import. Each entry carries the
  // tag/value plus optional per-ATTRIB overrides. An empty array means
  // the INSERT has no attribute data (group 66 = 0 in the DXF).
  attributes: z.array(blockInsertAttributeSchema).default([]),
  // T9b — the OWN group-5 handle of the SEQEND that terminates this INSERT's
  // ATTRIB sequence (emitted only when `attributes` is non-empty). R13+ SEQEND
  // is a real entity with its own handle + the INSERT's `330` owner (Autodesk
  // DXF reference SEQEND; ezdxf `Insert.add_attrib` → `SeqEnd` with a minted
  // handle). Imported verbatim from the source SEQEND; minted by the conformant
  // skeleton for a from-scratch INSERT with attributes.
  seqendHandle: z.string().optional(),
  // Dynamic block custom properties (Phase 3 — round-trip only). The
  // reducer populates this map when parsing DXFs that expose visibility
  // state / parameter overrides.
  customProperties: z
    .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
    .default({}),
  // Current visibility state for dynamic blocks. Undefined for static
  // blocks.
  visibilityState: z.string().optional(),
  // ── Annotative INSERT scale list (Phase FIN.2) ──
  // List of annotation scale names this INSERT applies to. AutoCAD
  // hides annotative INSERTs whose annotationScales list does NOT
  // contain the current CANNOSCALE. Empty array = INSERT is shown in
  // every cannoscale (matches AutoCAD's behaviour for non-annotative
  // blocks; for annotative blocks an empty list typically means "only
  // the default 1:1 context", which is left to the caller to seed).
  annotationScales: z.array(z.string()).default([]),
  // ── Per-INSERT representation overrides (Phase N.4) ──
  // AcDbBlockRepresentationData round-trip linkage. When an INSERT
  // diverges from the BlockDefinition defaults (parameter values or
  // visibility state), AutoCAD persists the divergence on a per-INSERT
  // ACDB_BLOCKREPRESENTATION_DATA object referenced via the INSERT's
  // 102 {ACAD_XDICTIONARY 360 <handle> 102 } extension dictionary.
  // The XData app `AcDbBlockRepETag` carries an etag (group 1071) that
  // pairs the INSERT with its representation data; the linkage handle
  // points at the OBJECTS-section ACDB_BLOCKREPRESENTATION_DATA record.
  //
  // `parameterOverrides` is the typed view of the per-INSERT divergence —
  // each key is a parameter name (matching the BlockDefinition's
  // dynamicMetadata.parameters[].name) whose effective value differs
  // from the block-level default. Resolvers (DynamicBlockParametersSection,
  // listDynamicParameters, evaluateBlock) walk the override map FIRST
  // and fall back to BlockDefinition defaults when a key is absent.
  // `customProperties` continues to carry the runtime active value
  // (kept for backward compatibility with DYN-CLI / evaluator) — the
  // override map exists in addition so the writer can emit the
  // correct AcDbBlockRepresentationData round-trip on save.
  representationOverrides: z
    .object({
      // Map of paramId → override value. Mirrors customProperties but
      // ONLY contains keys the user explicitly diverged on; absent
      // keys mean "inherit from BlockDefinition.dynamicMetadata".
      parameterOverrides: z
        .record(z.string(), z.union([z.string(), z.number(), z.boolean()]))
        .default({}),
      // Override visibility state if differs from default. Distinct
      // from `visibilityState` above — that field carries the active
      // runtime state (which may equal the default, hence not overridden);
      // this field is set ONLY when the user has chosen a non-default state.
      currentVisibilityStateId: z.string().optional(),
      // ETag value from AcDbBlockRepETag XData (group 1071) — used by
      // AutoCAD to pair the INSERT with its representation-data
      // dictionary entry. Round-trip only; opaque to our code.
      representationEtag: z.string().optional(),
      // Handle of the ACDB_BLOCKREPRESENTATION_DATA OBJECTS-section
      // record referenced via 102 {ACAD_XDICTIONARY 360 <handle> 102 }.
      // Round-trip only; opaque to our code.
      representationDataHandle: z.string().optional(),
    })
    .optional(),
  // ── Annotative INSERT contexts (PR-3) ──
  // Per-annotation-scale context snapshots — mirrors
  // `AcDbBlkRefObjectContextData` (block-reference variant). When an
  // annotative INSERT is repositioned/resized for a particular scale,
  // the per-scale insertion / scaleX / scaleY / rotation lives here.
  // Distinct from `annotationScales` (above) which is just the scale
  // name allow-list — `annotativeContexts` carries the per-scale
  // coordinate overrides the renderer applies via useCannoscaleResolver.
  annotativeContexts: z.array(annotScaleContextSchema).default([]),
  creationScaleName: z.string().optional(),
});

/* ── RECTANGULAR ASSOCIATIVE ARRAY ────────────────── */
export const arrayRectEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Rectangular Associative Array"),
  // ── S-ARRAYRECT-BASEPOINT-Z (audit §12) ──
  // The ARRAYRECT base point is the block-reference insertion point (DXF group
  // 10/20/30). Its Z elevation (group 30) round-trips through dxf-io (the
  // insertionPoint is a DxfPoint3D and the writer emits 30), but the BRIDGE
  // previously collapsed it to 2D (`toDrawingPoint`/`toDxfPoint`) so a non-zero
  // base-point Z was silently dropped on import→export. Promoted to
  // `drawingPointOptionalZSchema` (z?: number) — mirrors S-INSERT-INSERTPT-Z /
  // S-ARC-CIRCLE-CENTER-Z — so the bridge can carry the real Z while the many
  // 2D `{x, y}` basePoint literals built by reducers / compact store keep
  // typechecking. The bridge supplies z on import (`toDrawingPoint3D`) and emits
  // `z ?? 0` on export (`toDxfPoint3D`).
  basePoint: drawingPointOptionalZSchema,
  rowCount: z.number().int().min(1).default(3),
  columnCount: z.number().int().min(1).default(3),
  rowSpacing: z.number().default(50),
  columnSpacing: z.number().default(50),
  rowTotal: z.number().default(100),
  columnTotal: z.number().default(100),
  axisAngle: z.number().default(0),
  /**
   * @codex P2 (local review, round-4) — true iff `axisAngle` is DEFINITELY
   * in DEGREES because it was resolved from the AcDbAssocArray graph's NAMED
   * "AxesAngle" value param, which the AW-1 units seam
   * (`dxf-io/handleResolution.ts::axesAngleRadiansToModelDegrees`) converts
   * from AutoCAD's on-wire radians. Every OTHER case — non-associative (raw
   * group-50, degrees by construction), the graph never resolving an
   * axisAngle at all (raw anchor rotation, degrees), AND an associative
   * array resolved ONLY from the standalone
   * `ACDBASSOCARRAYRECTANGULARPARAMETERS` positional record (no fixture
   * proves that form's wire unit convention — deliberately NOT converted,
   * see the units-seam doc) — leaves this `false`/absent. Consumers that
   * need to know axisAngle's unit (`cad-core/common.ts::
   * rotateByArrayAxisAngle`/`arrayAxisAngleDeg`) read this instead of
   * re-deriving "which form supplied it" from `associative` alone (a
   * blanket `associative`-only check was itself a P2 regression this field
   * closes — a positional-only associative array's true unit is NOT
   * necessarily degrees just because SOME axisAngle value is present).
   */
  axisAngleFromNamedForm: z.boolean().optional(),
  associative: z.boolean().default(true),
  sourceEntityIds: z.array(z.string()).default([]),
  // ── SL-18 — source block-reference name (DXF group 2) ──
  // The `*U…` (or named) block the array's anchor block reference points at,
  // preserved VERBATIM from the imported `0 ARRAYRECT` so a NON-associative grid
  // can re-emit as a genuine MINSERT (`0 INSERT` + AcDbMInsertBlock) referencing
  // its real BLOCK_RECORD — instead of the former dangling `*ARRAY_<id>`
  // synthetic name AutoCAD/ezdxf could not resolve. OPTIONAL (absence-preserving):
  // set on import only when the source carried a non-empty group 2; a
  // reducer-built array with no source block stays a plain block reference. Used
  // ONLY by the non-associative MInsert emit (dxfBridge.arrayMinsert.ts) — the
  // associative block-ref path is untouched (preserve-source-never-infer).
  sourceBlockName: z.string().optional(),
  // ── Batch-8 S-ARRAYASSOC-MODEL — full ТЗ §9.4 AcDbAssocArray set ──
  // Every field below is OPTIONAL (or defaulted) so the existing
  // non-associative INSERT-grid arrays (which only set the scalars above) keep
  // validating. They are populated by the DXF bridge from the resolved
  // AcDbAssocArray graph (handleResolution.resolveAssocArrays).
  //
  // 3D / Z-level (3-axis rectangular array). AutoCAD's ARRAYRECT supports a
  // Z-stack of `levelCount` copies spaced `levelSpacing` apart.
  levelCount: z.number().int().min(1).optional(),
  levelSpacing: z.number().optional(),
  levelTotal: z.number().optional(),
  /** Per-row Z elevation increment (3D rectangular array). */
  rowElevation: z.number().optional(),
  /**
   * X / Y axis direction vectors. A real associative array stores the column
   * (X) and row (Y) axes as 3D vectors — NOT the single scalar `axisAngle`.
   * Modeled as typed point object-graphs per ПРИОРИТЕТ #1.
   */
  xAxisDirection: drawingPoint3DSchema.optional(),
  yAxisDirection: drawingPoint3DSchema.optional(),
  /** OCS normal of the array's base plane (group from rect-params record). */
  basePlaneNormal: drawingPoint3DSchema.optional(),
  /** Per-item rotation applied to every generated copy (radians). */
  itemRotation: z.number().optional(),
  /**
   * Driving-expression strings (e.g. "Columns" / "Rows" formula text). Opaque
   * round-trip — preserved verbatim from the AcDbAssocArray params record.
   */
  columnCountExpression: z.string().optional(),
  rowCountExpression: z.string().optional(),
  levelCountExpression: z.string().optional(),
  columnSpacingExpression: z.string().optional(),
  rowSpacingExpression: z.string().optional(),
  levelSpacingExpression: z.string().optional(),
  /**
   * Handle linkage to the AcDbAssocArray OBJECTS graph (action body / params /
   * dependencies). Round-trip only — keeps the entity ↔ graph wiring intact.
   */
  assocHandles: assocArrayHandlesSchema.optional(),
  /**
   * AW-0 — the §9.4 named-field snapshot the bridge decided at import time
   * (see `assocArrayParamsSnapshotSchema`). Round-trip-only / opaque to
   * model logic, same status as `assocHandles` above: the export bridge's
   * by-construction dirty-diff choke point (`dxfBridge.arrayAssocExport.ts`)
   * compares this against the entity's CURRENT typed fields to detect a
   * post-import mutation the AW-1 write-back encoder does not exist yet to
   * persist (docs/MASTERPLAN.md AW.2). Present only when
   * `applyAssocArrayResolution` actually ran for this entity.
   */
  assocParamsSnapshot: assocArrayParamsSnapshotSchema.optional(),
  /**
   * §9.4 SCALE — the EVALUATED per-item placement (position + rotation + scale
   * baked into an affine matrix) output by the action body. Typed object-graph,
   * NOT standalone scale scalars. OPTIONAL (not defaulted) so the thousands of
   * ArrayRectEntity literals built by reducers / compact store / tests keep
   * typechecking without supplying every new list — the bridge sets them on
   * import only when an associative graph is present.
   */
  itemTransforms: z.array(arrayItemTransformSchema).optional(),
  /**
   * ── Audit §12 S-ARRAYRECT-ITEMS-DECODE — the PERSISTED per-item records ──
   * The inline AcDbAssocArrayItem list decoded from the array's
   * ACDBASSOCARRAYACTIONBODY: verified 3D grid locator + flags
   * (erased / modified), the default transform in its SOURCE FORM
   * (translation point or full 4×4 matrix), the per-item relative-transform
   * OVERRIDE (moved/rotated items — where per-item rotation lands), the
   * entity-provider FK and the modify-body FK. OPTIONAL absence-preserving:
   * set on import only when the graph persisted a decodable item list (the
   * synthetic / standalone-params shapes carry none). Distinct from the
   * evaluator-facing `itemTransforms` above (flat-index EVALUATED output) —
   * `assocItems` is the verbatim source projection. See arrayAssoc.ts for
   * the full grammar reference + the render follow-up note.
   */
  assocItems: z.array(arrayAssocItemSchema).optional(),
  /**
   * Item → source-entity mapping (multi-source arrays). RESEARCHED DEFERRAL
   * (S-ARRAYRECT-ITEMS-DECODE): a per-item ORIGINAL-source map is NOT
   * persisted in DXF — the only persisted per-item entity link is
   * `assocItems[].entityProviderHandle` (the display block-reference); the
   * source entities are action-level AcDbAssocDependency FKs shared by every
   * item (`sourceEntityIds`). This home stays absent on import (never
   * derived) — it exists for a future ARRAYEDIT command-layer arm.
   */
  itemSourceMapping: z.array(arrayItemSourceMappingSchema).optional(),
  /** Per-item property / transform overrides (ARRAYEDIT). */
  itemOverrides: z.array(arrayItemOverrideSchema).optional(),
  /** Items the user replaced with a different source (ARRAYEDIT → Replace). */
  replacedItems: z.array(arrayReplacedItemSchema).optional(),
  /** Item indices the user erased (ARRAYEDIT → Erase). */
  erasedItems: z.array(z.number().int().min(0)).optional(),
});

/* ── VIEWPORT ─────────────────────────────────────── */
export const viewportEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Viewport"),
  center: drawingPointSchema,
  width: z.number().positive(),
  height: z.number().positive(),
  viewCenter: drawingPointSchema.default({ x: 0, y: 0 }),
  viewHeight: z.number().default(100),
  viewDirection: drawingPoint3DSchema.default({ x: 0, y: 0, z: 1 }),
  viewTarget: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
  viewTwistAngle: z.number().default(0),
  lensLength: z.number().default(50),
  frontClipZ: z.number().default(0),
  backClipZ: z.number().default(0),
  viewportStatus: z.number().int().default(1),
  displayLocked: z.boolean().default(false),
  frozenLayers: z.array(z.string()).default([]),
  // ── Per-viewport annotation scale (PR-13; carrier truth-corrected by
  //    S-VIEWPORT-A, audit §12.1) ─────────────────────────────────────
  // AutoCAD's VIEWPORT carries its own annotation scale. When set,
  // model-space content rendered through this viewport uses *this*
  // cannoscale rather than the document-level `header.cannoscaleName`.
  // Paper-space TEXT/MTEXT (entities living directly on the layout,
  // *not* inside a viewport) are NOT annotative — they always render at
  // full size.
  //
  // DXF CARRIER (truth-corrected): the per-VP annotation scale is NOT an
  // entity-level group code. PR-13 originally mapped DXF group 142 →
  // factor and 144 → "AcDbScale handle"; per the Autodesk DXF reference,
  // ezdxf 1.4.3 (entities/viewport.py acdb_viewport) and real AutoCAD
  // output (АБИ1.dxf), 142 is view CONTRAST (lighting block, with 141 =
  // brightness — see `brightness`/`contrast` below) and 144 is not a
  // VIEWPORT group code at all. The real carrier is the viewport's
  // extension-dictionary XRECORD "ASDK_XREC_ANNOTATION_SCALE_INFO"
  // (hard pointer 340 → AcDbScale record in ACAD_SCALELIST), which
  // round-trips verbatim through the OBJECTS passthrough for imported
  // files. KNOWN LIMITATION (documented per ПРИОРИТЕТ #1, plan дом:
  // audit §12.1 viewport rows + §11 OBJECTS audit-pass): resolving that
  // XRECORD into these fields on import, and re-synthesising it on
  // export after an in-session scale change, is the deferred follow-up —
  // the exporter deliberately emits NOTHING for these fields today
  // (never a phantom 142/144, never a dangling handle).
  //
  // All three fields are optional. When undefined the renderer falls
  // back to the document-level header values, so viewports without this
  // metadata round-trip identically.
  //
  // - `viewportCannoscaleName` — FK to `header.annotationScales[].name`
  //   (the user-visible label, e.g. "1:50"). Set in-session by the
  //   viewport-scale commands (command-engine viewportCommands).
  // - `viewportCannoscaleFactor` — cached numeric factor (paperUnits/
  //   drawingUnits, e.g. 0.02 for "1:50") so the renderer avoids a
  //   per-frame lookup against `annotationScales`.
  // - `viewportAnnoAllVisible` — overrides the document-level
  //   `header.annoAllVisible` *for content rendered through this
  //   viewport only*. Rarely set by users (no AutoCAD UI exposes it
  //   directly); preserved for round-trip when present.
  viewportCannoscaleName: z.string().optional(),
  viewportCannoscaleFactor: z.number().optional(),
  viewportAnnoAllVisible: z.boolean().optional(),
  // ── Lighting block — view brightness / contrast (S-VIEWPORT-A) ──────
  // DXF group 141 = view brightness, 142 = view contrast (floats,
  // 2007+/AC1021 lighting block — Autodesk DXF reference VIEWPORT; ezdxf
  // acdb_viewport `view_brightness`/`view_contrast`). Optional
  // absence-preserving: absent ⇒ the file had no lighting block and the
  // writer emits neither code. No render effect (plot/shaded-view
  // metadata); round-trip only.
  //
  // LEGACY OWN-EXPORT RECOVERY: files exported by THIS app in the PR-13
  // window (252db54b 2026-05-02 → S-VIEWPORT-A) carry a spec-corrupt
  // `142 ← viewportCannoscaleFactor`. The import bridge detects those by
  // our own EDIT_DWG_VP_OVERRIDES XDATA fingerprint (explicit scale codes
  // whose num/denom equals the incoming 142) and routes the value back to
  // `viewportCannoscaleFactor` instead of `contrast` — evidence-based, no
  // value-shape inference; the corrupt 142 is then dropped on re-export
  // rather than re-emitted as contrast. Full rationale + the residual
  // ambiguity note live at the recovery site (dxfBridge.drawingToDxf.ts,
  // VIEWPORT branch). Files without the fingerprint read 142 as contrast —
  // exactly what a real AutoCAD reader does with those bytes.
  brightness: z.number().optional(),
  contrast: z.number().optional(),
  // DXF group 69 — viewport identifier. Value 1 identifies the main
  // paper-space control viewport (covers the whole sheet; not a model-window).
  viewportId: z.number().int().optional(),
  // DXF group 90 — viewport status bit flags. Round-trips verbatim from/to
  // the DXF file so all flag bits (snap on, grid on, iso snap, etc.) are
  // preserved. Bit 0x4000 = display-locked. When present, toDxfEntity emits
  // group 90 with the lock bit kept in sync with `displayLocked`.
  statusBitFlags: z.number().int().optional(),
  // ── Snap / grid scalars (Batch-5 S-VIEWPORT-SNAPGRID) ──────────────
  // AcDbViewport snap/grid geometry. All optional — absent means the
  // viewport uses AutoCAD's per-document defaults. These survive a full
  // DXF round-trip (parser → typed → writer) instead of being dropped or
  // round-tripped only via the opaque extraGroups passthrough.
  // - `snapBase`    — DXF group 13/23 (snap base point, paper-space UCS).
  // - `snapSpacing` — DXF group 14/24 (snap X/Y spacing).
  // - `gridSpacing` — DXF group 15/25 (grid X/Y spacing).
  // - `snapRotation`— DXF group 50 (snap/grid rotation angle, DEGREES). Stored
  //   verbatim from the DXF file (group-code 50 angles are degrees, like the
  //   viewport `viewTwistAngle` group 51) — no rad/deg conversion at the
  //   parser, bridge or writer. Per the round-trip principle the source value
  //   is preserved exactly.
  // - `gridMajor`   — DXF group 61 (major grid line frequency, int).
  // - `circleZoomPercent` — DXF group 72 (circle zoom / smoothness percent,
  //   INTEGER group-code type — constrained to int so a fractional value can
  //   never be emitted as an invalid fractional group 72; mirrors gridMajor).
  snapBase: drawingPointSchema.optional(),
  snapSpacing: drawingPointSchema.optional(),
  gridSpacing: drawingPointSchema.optional(),
  snapRotation: z.number().optional(),
  gridMajor: z.number().int().optional(),
  circleZoomPercent: z.number().int().optional(),
  // ── Shade-plot / render mode (Batch-5 S-VIEWPORT-SHADEPLOT) ─────────
  // Plot-only metadata; no render effect. Round-trips so AutoCAD's plot
  // settings survive an edit. The view/edit UI (ViewportInspector dropdown +
  // geometry/viewport-set-shadeplot command) ships in VIEWPORT-SHADEPLOT-UI.
  // - `shadePlotMode` — DXF group 170. Authorable base modes:
  //   0=As Displayed, 1=Wireframe, 2=Hidden, 3=Rendered.
  //   Modes 4 (Visual Style) / 5 (Render Preset) reference a named
  //   visual-style / render-preset object handle (DXF 348 / 333) the data
  //   model does NOT yet carry — a documented follow-up scope
  //   (docs/test-gaps/viewport-shadeplot-visualstyle-modes.md). This field is
  //   left unbounded so an imported 4/5 round-trips verbatim; only the
  //   geometry/viewport-set-shadeplot COMMAND constrains AUTHORED writes to 0..3.
  // - `renderMode`    — DXF group 281 (0=2D Optimized/classic,1=Wireframe,
  //   2=HiddenLine, 3..6 shaded modes).
  shadePlotMode: z.number().int().optional(),
  renderMode: z.number().int().optional(),
  // Per-viewport override metadata parsed from EDIT_DWG_VP_OVERRIDES XDATA.
  // Stored on import so the full ViewportOverrides schema survives a round-trip:
  // scale (num/denom) and ucsName have no equivalent DXF geometry fields, so
  // without explicit storage they are dropped on the next DXF export.
  vpOverrideScale: z.object({ num: z.number(), denom: z.number() }).optional(),
  vpOverrideUcsName: z.string().optional(),
  // On-file `1071 vpOverridesWriterVersion` marker (viewportOverrides.ts
  // VP_OVERRIDES_WRITER_VERSION), preserved verbatim from import — pure
  // on-file evidence like vpOverrideScale/vpOverrideUcsName, never inferred.
  // Lets `resolveViewportFrozenLayerNames` POSITIVELY identify a v3+
  // (VP_OVERRIDES_CONSISTENT_NATIVE_331_VERSION) trustworthy-native viewport
  // by its OWN decoded XDATA, independent of the raw-document handle-keyed
  // `xdataAuthoritativeHandles` pre-scan — which cannot classify a viewport
  // whose native DXF `handle` is absent (@codex #514 P2 "Don't trust native
  // 331 for unclassified XDATA viewports"). Undefined for every pre-marker
  // file, matching `ViewportOverridesReadResult.writerVersion`.
  vpOverridesWriterVersion: z.number().optional(),
  // ── Native per-VP layer freeze + override soft-pointers (S-VIEWPORT-
  //    OVERRIDE-331, audit §12) ─────────────────────────────────────────
  // DXF group 331 — soft-pointer handles to the LAYER records frozen in THIS
  // viewport (multiple entries). The authoritative native representation of
  // per-VP layer freeze (Autodesk DXF reference AcDbViewport; ezdxf
  // `Viewport.frozen_layers` ← group 331). Distinct from the proprietary
  // EDIT_DWG_VP_OVERRIDES XDATA `frozenLayers` (layer NAMES), which the editor
  // uses for in-memory state; these are the file's native HANDLES, round-tripped
  // verbatim so a model import→export does not silently drop them. Resolving
  // handles → names (and unifying the two representations) is the separate,
  // deferred S-VIEWPORT-OVERRIDE-RESOLVE slice.
  frozenLayerHandles: z.array(z.string()).optional(),
  // PRESERVE-SOURCE round-trip carrier: the FULL, VERBATIM native group-331
  // handle list exactly as read on import (order + casing), snapshotted by
  // `resolveViewportFrozenLayerNames` BEFORE `retainUnresolvedFrozenLayerHandles`
  // clears the RESOLVED entries from `frozenLayerHandles` (kept double-count-safe
  // for the agent-bridge projector — @codex #486 P2). `frozenLayerHandles` itself
  // becomes the "unresolved/dangling only" carrier after reconciliation; THIS
  // field stays the byte-order/casing-faithful source the next export's native
  // 331 re-emission reads (@codex #514 P2 "Preserve verbatim 331 order before
  // clearing handles"). Absent for a from-scratch/in-editor viewport that never
  // went through import reconciliation — export then falls back to
  // `frozenLayerHandles` directly (unaffected, pre-existing behavior).
  sourceFrozenLayerHandles: z.array(z.string()).optional(),
  // DXF groups 335 / 343 / 344 / 91 — per-VP layer PROPERTY-override soft-/hard-
  // pointer references. Preserved as typed {code, handle} pairs (object-graph,
  // NOT a flat string bag) so the exact group code per entry round-trips
  // byte-faithfully (ПРИОРИТЕТ #1). Round-trip only — the semantic resolution
  // (handle → override VALUE + per-VP render) is the deferred RESOLVE slice.
  layerOverrideHandles: z
    .array(z.object({ code: z.number().int(), handle: z.string() }))
    .optional(),
  // ── Clipping-boundary handle (S-VIEWPORT-CLIP-HANDLE, EPIC-VIEWPORT-CLIP
  //    slice 1) ───────────────────────────────────────────────────────────────
  // DXF group 340 — native hard-pointer handle to the paperspace entity
  // (LWPOLYLINE / CIRCLE / ELLIPSE / closed SPLINE) defining this viewport's
  // non-rectangular clip boundary. Source of truth: ezdxf `Viewport`
  // `clipping_boundary_handle` (group 340, default "0") + Autodesk DXF reference
  // AcDbViewport. Round-tripped verbatim so a model import→export does not
  // silently drop a clipped viewport's boundary reference (ПРИОРИТЕТ #1).
  // Handle-preservation ONLY — parsing the clip path (SPATIAL_FILTER producer),
  // resolving the handle → boundary geometry (resolveViewportClip), and the
  // clip render are the separate, deferred EPIC-VIEWPORT-CLIP slices 2/3
  // (S-OBJECTS-SPATIAL-FILTER / S-VIEWPORT-CLIP-RESOLVE).
  clippingBoundaryHandle: z.string().optional(),
  // ── Resolved clip boundary + membership (S-VIEWPORT-CLIP-RESOLVE,
  //    EPIC-VIEWPORT-CLIP slice 3) ───────────────────────────────────────────
  // `clipPath` — the RESOLVED WCS clip polygon for this viewport, produced by
  // the OBJECTS second-pass resolver (handleResolution.resolveViewportClip):
  // VIEWPORT ext-dict → ACAD_FILTER dict → SPATIAL entry → SPATIAL_FILTER, whose
  // boundary points (rectangular 2-pt corners OR an explicit ≥3-pt polygon) are
  // transformed to world space by the filter's inverse-insert matrix (ezdxf
  // `xclip.py` chain + `spatial_filter.py` matrix). A TYPED object-graph
  // (DrawingPoint[]), NOT a boolean "isClipped" flag (ПРИОРИТЕТ #1). Absent ⇒
  // the viewport has no resolved XCLIP/VPCLIP boundary (rectangular full window).
  // The renderer projects this into the stencil clip path
  // (scene3d/viewports/viewportClipping.ts) via the viewport preview's
  // `__viewport_clip_path` property.
  clipPath: z.array(drawingPointSchema).optional(),
  // `clipBoundaryPaperSpace` — the native group-340 clip boundary polygon in the
  // viewport's PAPER-space frame (Codex #326 r4 #1). A VPCLIP boundary is a
  // paper-space entity FIXED on the sheet; the model view pans/zooms underneath
  // it (AutoCAD parity). `clipPath` is the DERIVED model-space projection of this
  // boundary through the viewport's current paper→model view transform — so when
  // the view changes (pan/zoom via applyViewportSetView / scale via
  // applyViewportSetScale) `clipPath` MUST be recomputed from this paper-space
  // source, or the paper boundary drifts and the stencil clips the wrong model
  // region. PRESENT only for native-340-sourced clips; a SPATIAL_FILTER clip is
  // fixed in MODEL/WCS space (its boundary points are already WCS via the
  // inverse-insert matrix), so it carries NO `clipBoundaryPaperSpace` and is NOT
  // recomputed on view change. A TYPED object-graph (DrawingPoint[]) per
  // ПРИОРИТЕТ #1 — this is the authoritative paper-space boundary, `clipPath` the
  // view-dependent projection.
  clipBoundaryPaperSpace: z.array(drawingPointSchema).optional(),
  // `clipBoundaryKind` — the SOURCE-preserved KIND of a resolved SPATIAL_FILTER
  // (XCLIP/VPCLIP) clip boundary: `"rectangular"` for a 2-point boundary,
  // `"polygonal"` for a >2-point one (dxf-io `resolveViewportClip` /
  // `expandSpatialFilterClipPolygon`). It CANNOT be re-derived from `clipPath`:
  // the resolver expands a 2-point rectangular boundary into a 4-corner polygon,
  // so `clipPath.length` is ≥3 for BOTH kinds. Preserved from the SPATIAL_FILTER
  // boundary point count at import time so the viewport inspector reports the
  // true clip mode for a polygon clip whose group-90 non-rectangular bit is 0
  // (some producers do not set it) — @codex #496 P3. A native-340 boundary
  // clip signals "polygonal" via `clipBoundaryPaperSpace` (the paper polygon)
  // instead, and this field is left undefined for it. Cleared together with the
  // other clip artefacts by `invalidateViewportClip` when the clip is removed /
  // superseded, so it never reports a stale kind. A TYPED enum, NOT a boolean
  // (ПРИОРИТЕТ #1).
  clipBoundaryKind: z.enum(["rectangular", "polygonal"]).optional(),
  // `childEntityIds` — the MEMBERSHIP index: model-space entity ids whose
  // geometry falls inside this viewport's view rectangle (the paper→model view
  // transform), produced by `buildViewportMembershipIndex`
  // (drawing-model/viewportMembership.ts). A TYPED id list (object-graph), NOT a
  // boolean. Consumed by the renderer's stencil-clipped child pass
  // (`__viewport_child_ids` preview property). Absent ⇒ membership not computed
  // (e.g. a viewport with no resolved clip / no model-space content).
  childEntityIds: z.array(z.string()).optional(),
});

export const drawingEntitySchema = z.discriminatedUnion("type", [
  lineEntitySchema,
  pointEntitySchema,
  circleEntitySchema,
  arcEntitySchema,
  ellipseEntitySchema,
  splineEntitySchema,
  lwpolylineEntitySchema,
  textEntitySchema,
  mtextEntitySchema,
  dimensionEntitySchema,
  multileaderEntitySchema,
  revisionCloudEntitySchema,
  wipeoutEntitySchema,
  hatchEntitySchema,
  blockInsertEntitySchema,
  arrayRectEntitySchema,
  viewportEntitySchema,
  // Legacy heavy POLYLINE, ACAD_TABLE and OLE2FRAME (./entityPolylineTableOle).
  // Their type literals ("Polyline" / "Table" / "OLE Frame") are unique within
  // this union — "Polyline" is deliberately distinct from LWPOLYLINE's
  // "LWPOLYLINE", because a heavy polyline is a different AutoCAD object (3D
  // polylines, polygon and poly-face meshes, per-vertex handles) and not a
  // second truth for the lightweight one.
  polylineEntitySchema,
  tableEntitySchema,
  oleFrameEntitySchema,
]);

/* ── TEXT STYLE ─────────────────────────────────── */
export const textStyleSchema = z.object({
  name: z.string(),
  fontName: z.string().default("txt"),
  fontFile: z.string().default(""),
  bigFontFile: z.string().default(""),
  height: z.number().default(0),
  widthFactor: z.number().default(1),
  obliqueAngle: z.number().default(0),
  flags: z.number().int().default(0),
  lastHeight: z.number().default(2.5),
  generationFlags: z.number().int().default(0),
  fontCharset: z.number().int().default(0),
  // ROOT E — whether the SOURCE STYLE record carried the
  // `100/AcDbSymbolTableRecord` + `100/AcDbTextStyleTableRecord` subclass
  // markers. `false` ⇒ a parsed R12 / marker-less record (the writer must NOT
  // synthesise the 100/ groups); `undefined` ⇒ a freshly-built / skeleton style
  // (the writer emits the R13+ default pair). Mirrors the LTYPE field.
  hadSubclassMarker: z.boolean().optional(),
  // T9b — the STYLE record's own group-5 handle + its `330` owner (the STYLE
  // table head). R13+ requires both on every symbol-table record (Autodesk DXF
  // reference; ezdxf writes them on every STYLE). Carried from an imported file
  // verbatim; minted / wired by the conformant skeleton for a from-scratch doc.
  // Absent on a freshly-built style until the conformant export assigns them.
  handle: z.string().optional(),
  ownerHandle: z.string().optional(),
});

/* ── DIMENSION STYLE ───────────────────────────── */
// Full set of AutoCAD DIMVAR fields with their DXF semantics. Defaults
// match AutoCAD's "Standard" dimension style as documented in the
// Autodesk DXF reference.
export const dimensionStyleSchema = z.object({
  name: z.string(),
  // ── Text ──
  DIMTXT: z.number().default(2.5), // 140: text height
  DIMTXSTY: z.string().default("Standard"), // 340: text style handle/name
  DIMCLRT: z.number().default(0), // 178: text color
  DIMTIH: z.boolean().default(true), // 73: text inside horizontal
  DIMTOH: z.boolean().default(true), // 74: text outside horizontal
  DIMTAD: z.number().default(0), // 77: text vertical placement
  DIMJUST: z.number().default(0), // 280: horizontal justification
  DIMGAP: z.number().default(0.625), // 147: gap around text
  DIMTVP: z.number().default(0), // 145: text vertical position offset
  DIMTSZ: z.number().default(0), // 142: tick size override (0 = use DIMASZ)
  // ── Arrows ──
  DIMASZ: z.number().default(2.5), // 41: arrow size
  DIMBLK: z.string().default(""), // 342: unified arrow block
  DIMBLK1: z.string().default(""), // 343: first arrow block
  DIMBLK2: z.string().default(""), // 344: second arrow block
  DIMLDRBLK: z.string().default(""), // 341: leader arrow block
  DIMSAH: z.boolean().default(false), // 173: separate arrow heads flag
  // ── Dimension lines & extension lines ──
  DIMCLRD: z.number().default(0), // 176: dim line color
  DIMCLRE: z.number().default(0), // 177: ext line color
  DIMLWD: z.number().default(-2), // 371: dim line weight (-2=ByBlock)
  DIMLWE: z.number().default(-2), // 372: ext line weight
  DIMEXO: z.number().default(0.625), // 42: ext line offset from origin
  DIMEXE: z.number().default(1.25), // 44: ext line extend past dim line
  DIMDLI: z.number().default(3.75), // 43: baseline spacing
  DIMDLE: z.number().default(0), // 46: dim line extend past ext line
  DIMSE1: z.boolean().default(false), // 75: suppress ext line 1
  DIMSE2: z.boolean().default(false), // 76: suppress ext line 2
  DIMSD1: z.boolean().default(false), // 281: suppress first dim line
  DIMSD2: z.boolean().default(false), // 282: suppress second dim line
  // ── Primary units ──
  DIMSCALE: z.number().default(1), // 40: overall scale factor
  DIMLFAC: z.number().default(1), // 144: linear measurement scale
  DIMDEC: z.number().default(4), // 271: decimal places (linear)
  DIMRND: z.number().default(0), // 45: rounding distance
  DIMLUNIT: z.number().default(2), // 277: linear unit format
  DIMPOST: z.string().default(""), // 3: prefix/suffix string
  DIMAPOST: z.string().default(""), // 4: angular suffix
  DIMDSEP: z.number().default(46), // 278: decimal separator (ASCII code, 46='.')
  DIMFRAC: z.number().default(0), // 276: fraction format (0=horizontal stacked, 1=diagonal, 2=not stacked)
  // ── Angular units ──
  DIMAUNIT: z.number().default(0), // 275: angular unit format
  DIMADEC: z.number().default(0), // 179: angular decimal places
  // ── Tolerances ──
  DIMTOL: z.boolean().default(false), // 71: enable tolerance display
  DIMLIM: z.boolean().default(false), // 72: enable limits display
  DIMTP: z.number().default(0), // 47: upper tolerance
  DIMTM: z.number().default(0), // 48: lower tolerance
  DIMTFAC: z.number().default(1), // 146: tolerance text scale
  DIMTOLJ: z.number().default(1), // 283: tolerance vertical justification
  DIMTDEC: z.number().default(4), // 272: tolerance decimal places
  // ── Fit / placement ──
  DIMATFIT: z.number().default(3), // 289: fit options for text+arrows
  DIMTOFL: z.boolean().default(false), // 172: force dim line between ext lines
  DIMSOXD: z.boolean().default(false), // 175: suppress arrows when not fitting
  DIMTIX: z.boolean().default(false), // 174: force text inside ext lines
  DIMTMOVE: z.number().default(0), // 279: text movement rules
  DIMUPT: z.boolean().default(false), // 288: user positioned text
  // ── Center marks ──
  // (positive = mark size, negative = line, 0 = none)
  DIMCEN: z.number().default(2.5), // 141
  // ── Zero suppression bitmask ──
  // (bit 1 = suppress leading, bit 2 = suppress trailing,
  //  bit 4 = suppress 0 feet, bit 8 = suppress 0 inches)
  DIMZIN: z.number().default(0), // 78: linear zero suppression
  DIMTZIN: z.number().default(0), // 284: tolerance zero suppression
  DIMAZIN: z.number().default(0), // 79: angular zero suppression
  // ── Alternate units ──
  DIMALT: z.boolean().default(false), // 170: enable alternate units
  DIMALTD: z.number().default(2), // 171: alternate decimal places
  DIMALTF: z.number().default(25.4), // 143: alternate scale factor
  DIMALTU: z.number().default(2), // 273: alternate unit format
  DIMALTRND: z.number().default(0), // 148: alternate rounding
  DIMALTTD: z.number().default(2), // 274: alternate tolerance decimals
  DIMALTZ: z.number().default(0), // 285: alternate zero suppression
  DIMALTTZ: z.number().default(0), // 286: alternate tolerance zero suppression
  // ── Annotative (Group C) ──
  // 0 = non-annotative, 1 = annotative. Stored in the DIMSTYLE DXF entry
  // via group 70 bit 8 (annotative-table-entry flag). When DIMANNO is
  // set, the renderer honours the entity's annotativeContexts[] and
  // picks the context matching state.drawing.annotationScale; otherwise
  // the style's fixed DIMTXT/DIMASZ/DIMSCALE values apply.
  DIMANNO: z.number().default(0),
  // ── Extra vars for round-trip ──
  extraDimVars: z
    .record(z.string(), z.union([z.string(), z.number()]))
    .default({}),
  // ROOT E — subclass-marker presence (see textStyleSchema.hadSubclassMarker).
  hadSubclassMarker: z.boolean().optional(),
  // SL-4 — the DIMSTYLE record's own group-105 handle, carried so a real R2018
  // DIMSTYLE round-trips its handle (import → model → re-export at code 105)
  // instead of dropping it at this boundary. Optional: a from-scratch /
  // handle-less style carries none (the writer then emits no record handle).
  // Pure round-trip metadata — not projected to the compact-store shell or the
  // render path, exactly like `hadSubclassMarker`.
  handle: z.string().optional(),
  // T9b — the DIMSTYLE record's `330` owner (the DIMSTYLE table head); same
  // round-trip-only contract as `handle`.
  ownerHandle: z.string().optional(),
});

/* ── MULTILEADER STYLE ─────────────────────────── */
// MLEADERSTYLE (AcDbMLeaderStyle) — full field set per AutoCAD DXF
// Reference 2018+ / ezdxf MultiLeader documentation. Every field that
// can appear in a DXF MLEADERSTYLE object has a home here; unknown
// codes round-trip through extraMlVars.
export const multileaderStyleSchema = z.object({
  name: z.string(),
  contentType: z.number().default(2), // 170
  textStyleName: z.string().default("Standard"), // 342 (text_style_handle → text style name)
  textHeight: z.number().default(2.5), // 40
  textColor: z.string().default("ByBlock"), // 93
  textAttachmentType: z.number().default(1), // 172
  // SL-5 F1 (@codex round-6) — text_right_attachment_type (ezdxf 1.4.3
  // `acdb_mleader_style` group 178, default 1). The parser reads 178 into a typed
  // dxf-io field; without a model home the DrawingModel bridge DROPPED it on the
  // app import→export path (174 = text_left_attachment_type has `textAttachment
  // Type`; 178 had none). OPTIONAL (no default) so a source WITHOUT 178 stays
  // byte-stable — the field is set only when the source carried it, never inferred.
  textRightAttachmentType: z.number().int().optional(), // 178
  textAttachmentDirection: z.number().int().default(0), // 271
  textLineSpacingStyle: z.number().int().default(1), // 73
  textLineSpacingFactor: z.number().default(1), // 45
  textLineSpacingDistance: z.number().default(0), // derived / extension
  textFrameDisplay: z.boolean().default(false), // 292
  textWidth: z.number().default(0), // 41
  leaderType: z.number().default(1), // 173 — leader_type (0=invisible/1=straight/2=spline)
  leaderLineType: z.string().default("ByBlock"), // 340 (leader_linetype_handle → LTYPE name)
  leaderColor: z.string().default("ByBlock"), // 91
  leaderWeight: z.string().default("ByBlock"), // 90
  landingEnabled: z.boolean().default(true), // 290
  doglegEnabled: z.boolean().default(true), // 291
  // SL-5 P3b — CORRECTED code map (ezdxf acdb_mleader_style): 43 = dogleg_length,
  // 42 = landing_gap_size. The prior comments swapped them (doglegLength // 42,
  // landingGap // 43), and the bridge conflated doglegLength ← landingDistance(42)
  // while dogleg(43) was dropped → a non-default dogleg re-imported as 8.
  doglegLength: z.number().default(8), // 43 — dogleg_length
  landingGap: z.number().default(2), // 42 — landing_gap_size
  // SL-5 P3b — 46 = align_space (ezdxf acdb_mleader_style, default 4). Previously
  // NOT modelled: import conflated it into landingGap and export wrote landingGap
  // to 46, so 46 masqueraded as the landing gap. Now its own field so 42/43/46 all
  // round-trip with correct semantics through the model path.
  alignSpace: z.number().default(4), // 46 — align_space
  maxLeaderSegments: z.number().default(2), // 90 (segments)
  arrowheadSize: z.number().default(2.5), // 44
  arrowheadBlock: z.string().default(""), // 341 (arrow_head_handle → block name)
  blockName: z.string().default(""), // 343 (block_record_handle → block name)
  blockScale: z.number().default(1), // 47
  blockColor: z.string().default("ByBlock"), // 93 (block)
  blockConnectionType: z.number().int().default(0), // 177 (center extents / insertion)
  blockRotation: z.number().default(0), // 48
  // PR-14 — renamed from annotativeEnabled to enableAnnotation to match
  // AutoCAD MLEADERSTYLE DXF group 296 / AcDbMLeaderStyle property name.
  // This IS a legit on-disk DXF flag (analogous to DIMSTYLE.DIMANNO) — it
  // controls whether newly-created multileaders inherit the current
  // annotation scale into their annotativeContexts[] array.
  enableAnnotation: z.boolean().default(false), // 296
  overallScale: z.number().default(1), // 142
  breakEnabled: z.boolean().default(true), // 293
  breakGapSize: z.number().default(3.75), // 143
  breakSize: z.number().default(3.75), // 44 alt / dimension break size
  basePoint: drawingPointSchema.optional(),
  // SL-5 F6 (@codex round-2) — the source MLEADERSTYLE object's group-5 handle
  // for an IMPORTED style. The export REUSES this handle for the re-emitted
  // object (mint only when absent = genuinely authored) so save→reopen→save is
  // byte-stable for the object handle + its ACAD_MLEADERSTYLE dict child, rather
  // than re-minting each cycle and orphaning raw reactors / dict children still
  // pointing at the prior handle. Never emitted as a group itself — it is the
  // group-5 identity carried forward.
  sourceHandle: z.string().optional(),
  // Round-trip escape hatch for unknown MLEADERSTYLE group codes
  extraMlVars: z.record(z.string(), z.string()).default({}),
});

/* ── BLOCK DEFINITIONS (from BLOCKS section) ───────── */

// Decoded DXF group 70 flag bits for ATTDEF / ATTRIB entities. Each
// field maps 1:1 to a documented AutoCAD behaviour. The raw integer
// group 70 is reconstructed on export from these booleans so a
// partial-write doesn't desync the on-disk flag.
export const attdefFlagsSchema = z.object({
  invisible: z.boolean().default(false), // bit 1 (value 1)
  constant: z.boolean().default(false), // bit 2 (value 2)
  verify: z.boolean().default(false), // bit 3 (value 4)
  preset: z.boolean().default(false), // bit 4 (value 8)
  lockPosition: z.boolean().default(false), // bit 5 (value 16)
  multiline: z.boolean().default(false), // bit 6 (value 32)
});
export type AttdefFlags = z.infer<typeof attdefFlagsSchema>;

// Full ATTDEF entity. Extends the legacy subset (handle, tag, prompt,
// defaultValue, insertionPoint, height, rotation, styleName) with every
// AutoCAD-documented ATTDEF / ATTRIB field so round-trip through DXF
// preserves alignment, width factor, oblique angle, justification, flag
// bits and MTEXT-ATTDEF background fill.
export const attdefEntitySchema = drawingEntityBaseSchema.extend({
  type: z.literal("Attdef"),
  handle: z.string().default(""),
  tag: z.string().default(""),
  prompt: z.string().default(""),
  defaultValue: z.string().default(""),
  insertionPoint: drawingPointSchema,
  alignmentPoint: drawingPointSchema.optional(), // DXF 11/21
  height: z.number().default(2.5),
  rotation: z.number().default(0),
  styleName: z.string().default("Standard"),
  widthFactor: z.number().default(1), // DXF 41
  obliqueAngle: z.number().default(0), // DXF 51
  generationFlags: z.number().int().default(0), // DXF 71
  hJustification: z.number().int().default(0), // DXF 72
  vJustification: z.number().int().default(0), // DXF 74
  flags: attdefFlagsSchema.default(() => attdefFlagsSchema.parse({})), // DXF 70 decomposed
  backgroundFill: z.boolean().default(false), // DXF 90 != 0
  backgroundColor: z.string().optional(),
  fillBoxScale: z.number().default(1.5), // DXF 45
  // DXF group 210/220/230 — extrusion direction (OCS normal). ATTDEF is an
  // OCS entity (parity with TEXT). Optional store (S-EXTRUSION-STORE); absent
  // ⇒ (0,0,1) default. OCS→WCS render is a later slice — this slice only
  // round-trips the field.
  extrusion: drawingPoint3DSchema.optional(),
  // ── Annotative (PR-1) ──
  // Per-annotation-scale context snapshots for ATTDEF — mirrors
  // `AcDbBlkRefObjectContextData` (attribute variant). When an ATTDEF
  // inside an annotative block carries scale-specific overrides, each
  // context records the per-scale insertion / rotation / attribTag.
  annotativeContexts: z.array(annotScaleContextSchema).default([]),
  creationScaleName: z.string().optional(),
});

// A single entity inside a block definition. Blocks can contain the
// same supported primitives as model space (LINE, CIRCLE, ARC, TEXT,
// LWPOLYLINE, INSERT, …) plus ATTDEF records that carry the block's
// attribute placeholders.
export const blockChildEntitySchema = z.discriminatedUnion("type", [
  lineEntitySchema,
  pointEntitySchema,
  circleEntitySchema,
  arcEntitySchema,
  ellipseEntitySchema,
  splineEntitySchema,
  lwpolylineEntitySchema,
  textEntitySchema,
  mtextEntitySchema,
  blockInsertEntitySchema,
  attdefEntitySchema,
  // Batch-7 (S-BLOCKCHILD-HATCH / -DIM / -MLEADER): HATCH, DIMENSION and
  // MULTILEADER are valid block-definition children per the Autodesk DXF
  // reference (the BLOCKS section has the identical structure to ENTITIES)
  // and ezdxf BlockLayout. Their type literals ("Hatch" / "Dimension" /
  // "Multileader") are unique within this union (verified against every
  // member's z.literal). Adding them here lets the importer preserve a
  // parsed block-child of these types through import→export instead of
  // dropping it at the allow-list. Render of these children is owned by a
  // separate DEFUSE slice; this is backend import-preservation only.
  hatchEntitySchema,
  dimensionEntitySchema,
  multileaderEntitySchema,
  // SL-2 (@codex #422 P2-A): a REVISION CLOUD is a legal block-definition child
  // — AutoCAD stores it as an LWPOLYLINE (arc bulges) carrying a RevcloudProps
  // XData marker, and the BLOCKS section has the identical structure to
  // ENTITIES, so a revcloud placed inside a block is valid per the Autodesk DXF
  // reference + ezdxf BlockLayout. Its type literal ("Revision Cloud") is unique
  // within this union. Without it, a block-child revcloud (stored `0 LWPOLYLINE`
  // + RevcloudProps, reclassified to REVCLOUD on read) was DROPPED at the
  // `dxfBlockDefinitionToDrawingBlock` allow-list on import → lost on export,
  // inconsistent with a top-level revcloud (which round-trips). Render of a
  // block-child revcloud is a separate DEFUSE concern (same as the Batch-7
  // Hatch/Dimension/Multileader children above); this keeps import→export
  // preservation.
  revisionCloudEntitySchema,
  // A heavy POLYLINE, an ACAD_TABLE and an OLE2FRAME are all legal
  // block-definition children — the BLOCKS section has the identical structure
  // to ENTITIES (Autodesk DXF reference; ezdxf BlockLayout), and this repo's
  // own `apps/web/public/abi1-test.dxf` fixture carries eleven heavy POLYLINEs
  // INSIDE block definitions. Without them a block-child polyline would be
  // dropped at the `dxfBlockDefinitionToDrawingBlock` allow-list on import and
  // lost on export, inconsistent with a top-level one.
  polylineEntitySchema,
  tableEntitySchema,
  oleFrameEntitySchema,
]);

// XREF resolution status (Phase FIN.3 — XREF suite). A block becomes an
// xref when its `flags` carry bit 4 (xref) or bit 8 (xref-overlay). The
// status mirrors AutoCAD's XrefManager state column:
//   - "resolved"      → the external file was loaded and entities are
//                        rendered normally
//   - "unloaded"      → user explicitly XUNLOAD'ed the xref; the record
//                        and INSERTs survive, but the entities are
//                        suppressed and a grey placeholder is rendered
//   - "not-found"     → resolution failed (e.g. file deleted on disk);
//                        a red dashed placeholder is shown so the user
//                        can XRELOAD with a fresh path
//   - "unreferenced"  → the xref is no longer used by any INSERT; AutoCAD
//                        flags it for cleanup but the record stays until
//                        XDETACH or PURGE
//   - "orphaned"      → dependency xref of a missing parent xref
export const xrefStatusSchema = z
  .enum(["resolved", "unloaded", "not-found", "unreferenced", "orphaned"])
  .default("resolved");
export type XrefStatus = z.infer<typeof xrefStatusSchema>;

// Raw DXF groups for entities we do not yet fully parse into our typed
// schema (ACAD_TABLE, SPLINE legacy variants, XRECORD, ACAD_PROXY_ENTITY,
// 3DFACE, SOLID, RAY, XLINE, IMAGE, …) but must preserve verbatim for
// round-trip fidelity (E2 / CLAUDE.md "НИЧЕГО НЕ УПРОЩАТЬ"). The parser
// captures every DXF group of the unsupported entity, starting with the
// entity-type header (code 0) excluded — `entityType` carries that value
// separately. On export the writer re-emits `{code: 0, value: entityType}`
// followed by `groups[]`. Stored per-block for BLOCK definition children
// and per-DrawingDocument for ENTITIES-section siblings.
export const rawEntityGroupSchema = z.object({
  entityType: z.string().min(1),
  groups: z.array(
    z.object({
      code: z.number().int(),
      value: z.string(),
    }),
  ),
});
export type RawEntityGroup = z.infer<typeof rawEntityGroupSchema>;

// Saved view extracted from the VPORT `*Active` record of a DXF file
// (TABLES section). AutoCAD persists the last interactive view here and
// re-applies it on open. We mirror the AutoCAD-documented fields
// (group codes 12/22 — view center in DCS, 40 — view height, 41 —
// viewport aspect ratio) plus the optional 17/27/37 view target and
// 16/26/36 view direction.
//
// The bridge importer only emits savedView for plan-view DXFs (viewDir
// = (0,0,1) or absent) — see `extractSavedViewFromRawTableGroups`. The
// `viewDirection` field is preserved here for downstream readers /
// debuggability even though current consumers (orthographic camera
// applier) only need the center/height/aspect.
export const savedViewSchema = z.object({
  center: drawingPointSchema,
  height: z.number().positive(),
  aspectRatio: z.number().positive(),
  target: drawingPoint3DSchema.optional(),
  viewDirection: drawingPoint3DSchema.optional(),
});
export type SavedView = z.infer<typeof savedViewSchema>;

export const blockDefinitionSchema = z.object({
  name: z.string().min(1),
  handle: z.string().default(""),
  ownerHandle: z.string().optional(),
  // T9b — the matching ENDBLK's OWN group-5 handle. R13+ ENDBLK is an
  // entity-like record with its own handle + a `330` owner equal to the
  // BLOCK's owner (Autodesk DXF reference; ezdxf BlockLayout). Imported from
  // the source ENDBLK (`endblkGroups`), minted by the conformant skeleton for
  // every from-scratch block; the writer reconstructs `0 ENDBLK / 5 / 330 /
  // 100 AcDbEntity / 8 / 100 AcDbBlockEnd` from it.
  endblkHandle: z.string().optional(),
  layer: z.string().default("0"),
  flags: z.number().int().default(0),
  basePoint: drawingPoint3DSchema,
  xrefPathName: z.string().default(""),
  description: z.string().default(""),
  entities: z.array(blockChildEntitySchema).default([]),
  // Dynamic block authoring metadata (Phase DYN-1). Undefined for static
  // blocks; populated for dynamic blocks with parameters / actions /
  // visibility states. The evaluator (Subagent DYN-1.5) consumes this
  // field to produce resolvedEntities per INSERT instance.
  dynamicMetadata: dynamicBlockMetadataSchema.optional(),
  // Phase K.5 — full-fidelity AutoCAD-spec block authoring data
  // (parameters, actions, eval graph, visibility states with strict
  // DXF group-code mapping). Complements `dynamicMetadata` (which is
  // the evaluator-friendly normalisation): `authoringData` is the
  // DXF source of truth, used by the writer to round-trip every
  // authored field byte-for-byte. Undefined for static blocks and
  // for blocks parsed before Phase K.5 wiring.
  authoringData: blockAuthoringDataSchema.optional(),
  // Whether this block is dynamic (has an AutoCAD 2007+ eval-graph /
  // ACAD_ENHANCEDBLOCK extension). Detected from BLOCK_RECORD extension
  // dictionary, XData (AcDbBlockRepETag / AcDbDynamicBlockTrueName /
  // AcDbDynamicBlockGUID) or OBJECTS-section eval-graph records.
  // Independent of `dynamicMetadata`: the flag can be `true` while the
  // typed metadata is still absent (we detect dynamism even when the
  // eval graph is beyond our current parser tier — Track A fallback).
  // See packages/drawing-model/src/dxfBridge.dynamic.detect.ts.
  //
  // Optional rather than required-with-default so existing test fixtures
  // that build BlockDefinition partials (before zod-parse) keep compiling.
  // Consumers must treat `undefined` as `false` via `?? false` — the
  // importer pipeline always sets an explicit value.
  dynamicBlock: z.boolean().optional(),
  // Phase FIN.3 — XREF suite. Static blocks default to "resolved" (the
  // entities live inline in `entities`); xref blocks reflect the live
  // resolution state of the external file. Required for XrefManager UI
  // and the not-found / unloaded indicators in blockRender.
  xrefStatus: xrefStatusSchema,
  // Cached parsed content of the resolved xref. Populated by XATTACH /
  // XRELOAD; cleared by XUNLOAD. Stored as `unknown` because the actual
  // shape is the parsed DXF document we reuse on re-render — we keep
  // the loose typing to avoid pulling DXF parser types into
  // drawing-model and creating a circular dependency.
  xrefLoadedContent: z.unknown().optional(),
  // Raw DXF groups for unsupported children (ACAD_TABLE, SPLINE legacy,
  // XRECORD, …) kept verbatim so the DXF writer can re-emit them
  // unchanged and preserve full-fidelity round-trip. Populated by
  // dxfBlockDefinitionToDrawingBlock from the parser's
  // DxfUnsupportedEntity records. See also the top-level
  // DrawingDocument.rawEntityGroups for the ENTITIES-section analogue.
  //
  // Optional rather than required-with-default so existing test fixtures
  // and command-engine BlockDefinition partials keep compiling — the
  // importer / bridge pipeline always populates an explicit array; direct
  // consumers treat `undefined` as `[]`.
  rawEntityGroups: z.array(rawEntityGroupSchema).optional(),
});

// ── BLOCK_RECORD (TABLES-section metadata mirror of BlockDefinition) ──
// Every BlockDefinition has a matching BlockRecord in the TABLES section
// storing metadata that does not live inside the BLOCK record itself:
// default insertion units (INSUNITS), explodability, uniform vs non-
// uniform scaling, associated LAYOUT handle (for paper-space blocks)
// and the annotative flag. The BlockRecord is the authoritative source
// for this metadata — the BlockDefinition description is mirrored only
// for convenience. Keyed by block name in DrawingDocument.blockRecords.
export const blockRecordSchema = z.object({
  name: z.string().min(1),
  // DYN-4 Phase 6 — BLOCK_RECORD entry handle (DXF group 5). Required for
  // round-trip fidelity of the BLOCK_RECORD → extension-dict →
  // ACAD_ENHANCEDBLOCK → eval_graph chain; without it, re-import cannot
  // rebuild dynamic-block metadata from a writer-produced DXF.
  handle: z.string().optional(),
  // T9b — BLOCK_RECORD `330` owner = the BLOCK_RECORD table-head handle
  // (Autodesk DXF reference; ezdxf writes `330 9` on every record). Imported
  // verbatim; wired to the adopted skeleton head by the conformant export.
  ownerHandle: z.string().optional(),
  // Ceiling is 21 (US Survey Feet), matching the document-level $INSUNITS
  // cap (see header `insunits` below). Previously capped at 20, which
  // rejected survey-foot BLOCK_RECORD 70=21 at the schema gate. (Codex#248
  // F2 P2.)
  insertionUnits: z.number().int().min(0).max(21).default(0), // DXF 70
  explodable: z.boolean().default(true), // DXF 280
  scalable: z.enum(["uniform", "non-uniform"]).default("non-uniform"), // DXF 281
  layoutHandle: z.string().optional(), // DXF 340
  // DYN-4 Phase 6 — pointer to the BLOCK_RECORD's extension DICTIONARY
  // (DXF group 360 inside the 102/{ACAD_XDICTIONARY/102/} nested block).
  // Dynamic blocks chain BLOCK_RECORD.extensionDictionaryHandle → DICT
  // with ACAD_ENHANCEDBLOCK entry → ACAD_EVALUATION_GRAPH → parameters.
  // Without this field being preserved across round-trip, the writer
  // drops the link and re-imports see static blocks.
  extensionDictionaryHandle: z.string().optional(),
  // Batch-6 S-BLOCKRECORD-REACTORS — persistent-reactor handles from the
  // BLOCK_RECORD's `102 {ACAD_REACTORS … 330 <handle> 102 }` nested block.
  // Captured verbatim so the app import→export path round-trips the
  // reactor chain (the dxf-io writer re-emits these via the same field on
  // DxfBlockRecordEntry). Omitted when empty/absent. (Codex#248 F1 P2.)
  reactorHandles: z.array(z.string()).optional(),
  isAnnotative: z.boolean().default(false), // XData ACAD
  // ── Annotative decomposition map (PR-1) ──
  // For annotative blocks, AutoCAD synthesises an anonymous block
  // (`*A_NNN` naming convention) for each annotation scale that has
  // been materialised. The map records `scaleName → anonymousBlockHandle`
  // so the writer can round-trip the `AcadAnnotativeDecomposition`
  // marker without re-walking the OBJECTS section. Empty for non-
  // annotative or freshly-materialised blocks.
  annotativeDecompositions: z.record(z.string(), z.string()).default({}),
  description: z.string().default(""), // mirror of BLOCK header
  // Raw payload for dynamic-block round-trip. Phase 3 populates this
  // with ACDBBLOCK* OBJECTS groups. Static blocks leave it undefined.
  dynamicBlockData: z.unknown().optional(),
});
export type BlockRecord = z.infer<typeof blockRecordSchema>;

/* ── LAYOUT (OBJECTS-section LAYOUT entity) ───────── */
// Every paper-space tab in a DXF is a LAYOUT object inside the OBJECTS
// section, owned by a `*Paper_Space`/`*Paper_Space<N>` BLOCK_RECORD via
// a handle pair (LAYOUT group 330 → BLOCK_RECORD, BLOCK_RECORD group 340
// → LAYOUT). The LAYOUT object carries plot settings (paper size, plot
// origin, plot rotation) and layout limits/extents. AutoCAD's tab bar
// renders each LAYOUT by `name`, not by the anonymous block name, so the
// dialog / InsertBlockDialog filter and the status-strip tab strip need
// access to the mapping.
export const layoutSchema = z.object({
  name: z.string().min(1), // group 1 — tab name ("Model", "Layout1", …)
  handle: z.string().default(""), // group 5 — LAYOUT object handle
  ownerBlockRecordHandle: z.string().default(""), // group 330 (AcDbLayout) — owning BLOCK_RECORD
  // group 330 (AcDbPlotSettings-leading context) — the ACAD_LAYOUT DICTIONARY
  // that owns this LAYOUT object. Distinct from `ownerBlockRecordHandle`.
  // Optional: most layouts round-trip via the BLOCK_RECORD link alone; the
  // conformant-skeleton synthesis sets it so a minted paper tab is owned by the
  // skeleton's ACAD_LAYOUT dictionary (ROOT C).
  dictionaryOwnerHandle: z.string().optional(),
  tabOrder: z.number().int().default(0), // group 71
  // ── Full per-layout PLOTSETTINGS (PDF-6.1) ──
  // `plotSettings` is the AUTHORITATIVE per-layout plot configuration (the
  // complete AcDbPlotSettings sub-object: paper w/h 44/45, margins 40-43,
  // area type 74, window 48/49/140/141, scale 142/143, flags 70, plot-style
  // 7, page-setup 1). The vector PDF exporter reads it directly. The four
  // flat fields below (paperSize/plotPaperUnits/plotRotation/plotOrigin) are
  // a DERIVED MIRROR kept for the scene/camera page-setup path
  // (`extractPageSetup`, which reads limits + units) and the dxf-io transport
  // shape; the bridge keeps them in sync with `plotSettings` on import. They
  // are NOT an independent source of truth — see dxfBridge.layouts.ts.
  plotSettings: layoutPlotSettingsSchema.default(() =>
    layoutPlotSettingsSchema.parse({}),
  ),
  // Plot settings — flat mirror (DXF group codes documented on each field)
  paperSize: z.string().default(""), // group 4 — paper-size name
  plotPaperUnits: z.number().int().default(0), // group 72 — 0=inches, 1=mm, 2=pixels
  plotRotation: z.number().int().default(0), // group 73 — 0/1/2/3 (0°, 90°, 180°, 270°)
  plotOrigin: drawingPointSchema.default({ x: 0, y: 0 }), // group 46/47
  // Layout limits / extents
  minimumLimits: drawingPointSchema.default({ x: 0, y: 0 }), // group 10/20
  maximumLimits: drawingPointSchema.default({ x: 420, y: 297 }), // group 11/21
  insertionBase: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }), // group 12/22/32
  minimumExtents: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }), // group 14/24/34
  maximumExtents: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }), // group 15/25/35
  elevation: z.number().default(0), // group 146
  orthographicViewType: z.number().int().default(0), // group 76
  // round-17 BUG 3 — nested `102` reactor / xdictionary block(s) in the LAYOUT
  // HEADER slot (after the 5/330 owner, BEFORE the first `100` subclass marker).
  // dxf-io's `parseLayoutEntry` captures these into `DxfLayoutEntry.headerExtraGroups`
  // (a SEPARATE positional bucket the writer re-emits in the original slot,
  // distinct from the tail `extraGroups`) — but the bridge previously DROPPED them
  // because the model `Layout` had no carrier, so an import→export of a LAYOUT with
  // header-slot 102 reactor/xdictionary groups LOST them despite the dxf-io support.
  // The shape is a drawing-model-LOCAL `{ code, value }` array structurally
  // identical to dxf-io's `DxfGroup` (model.ts is the canonical core and must NOT
  // import from packages/dxf-io); the bridge maps `DxfGroup[]` ↔ this shape with a
  // plain array copy. Optional (undefined when absent) so existing layouts/tests
  // are unaffected.
  headerExtraGroups: z
    .array(z.object({ code: z.number().int(), value: z.string() }))
    .optional(),
});
export type Layout = z.infer<typeof layoutSchema>;

/* ── LAYER DEFINITIONS ─────────────────────────── */
export const layerDefinitionSchema = z.object({
  name: z.string().min(1),
  color: z.number().int().default(7), // ACI index; 7 = white/black
  visible: z.boolean().default(true), // On/Off
  frozen: z.boolean().default(false),
  locked: z.boolean().default(false),
  linetype: z.string().default("Continuous"),
  lineweight: z.number().default(-3), // -3 = Default
  plottable: z.boolean().default(true),
  description: z.string().default(""),
  flags: z.number().int().default(0),
  handle: z.string().default(""),
  // T9b — the LAYER record's `330` owner (the LAYER table head), its `390`
  // PlotStyleName object (R2000+; AutoCAD / ezdxf default = the `Normal`
  // ACDBPLACEHOLDER of the ACAD_PLOTSTYLENAME dictionary) and its `347`
  // MATERIAL object (R2007+; default = the `ByLayer` MATERIAL). Imported
  // verbatim; resolved from the skeleton template's OBJECTS by the conformant
  // export for a from-scratch layer. Never fabricated when the target is absent.
  ownerHandle: z.string().optional(),
  plotStyleHandle: z.string().optional(),
  materialHandle: z.string().optional(),
});
export type LayerDefinition = z.infer<typeof layerDefinitionSchema>;

/* ── STYLE TABLES ──────────────────────────────── */
// Pre-parse the default entries through their own schemas so every
// nested default (including new MLEADERSTYLE fields like
// leaderLineType, textAttachmentDirection, basePoint, extraMlVars) is
// actually populated on the resulting literal. zod v4 does NOT
// recursively re-parse values passed to `.default(...)`, so we have
// to eagerly build fully-populated literals here.
const defaultTextStyle = textStyleSchema.parse({ name: "Standard" });
const defaultDimensionStyle = dimensionStyleSchema.parse({ name: "Standard" });
const defaultMultileaderStyle = multileaderStyleSchema.parse({
  name: "Standard",
});
const defaultLayer0 = layerDefinitionSchema.parse({ name: "0" });

export const styleTablesSchema = z.object({
  textStyles: z.array(textStyleSchema).default([defaultTextStyle]),
  dimensionStyles: z
    .array(dimensionStyleSchema)
    .default([defaultDimensionStyle]),
  multileaderStyles: z
    .array(multileaderStyleSchema)
    .default([defaultMultileaderStyle]),
  layers: z.array(layerDefinitionSchema).default([defaultLayer0]),
});

export const drawingSpaceSchema = z.object({
  id: z.string().min(1),
  kind: drawingSpaceKindSchema,
  name: z.string().min(1),
  entities: z.array(drawingEntitySchema),
  // ── Block-edit session fields (Phase 2.3) ──
  // Populated only for spaces with kind === "block-edit". The session
  // targets an entry in DrawingDocument.blocks[blockEditName]; the
  // entities visible while editing live in blockDef.entities (NOT in
  // the space's `entities` array — that array stays empty during
  // BEDIT). On enter, the reducer writes a JSON snapshot of the block
  // definition into blockEditSnapshot so a subsequent BCLOSE / discard
  // can restore the original shape.
  blockEditName: z.string().optional(),
  blockEditSnapshot: z.unknown().optional(),
  // Previous active space id, captured on enter-block-edit so
  // exit-block-edit can restore the caller's active-space selection
  // (e.g. user was in Layout1 when BEDIT started — after BCLOSE they
  // return to Layout1, not to Model).
  blockEditPreviousSpaceId: z.string().optional(),
  // ── REFEDIT session fields (Phase FIN.1) ──
  // Populated only for spaces with kind === "refedit". REFEDIT is an
  // in-place block edit: the user picks an INSERT in the host space,
  // the system opens an editing session on the referenced block while
  // keeping the surrounding host-space geometry visible (but tinted).
  // Mutations to entities of the working set propagate back into
  // blocks[refeditBlockName].entities (analogous to BEDIT). The
  // surrounding entities are not part of the working set and may NOT
  // be edited — selection / tools / preview operations filter them out.
  refeditBlockName: z.string().optional(),
  // Id of the INSERT entity from which the REFEDIT session was started.
  // Used for status/UX (display "Editing reference: {block} via INSERT
  // {id}") and to highlight the originating instance.
  refeditSourceInsertId: z.string().optional(),
  // Entity ids that participate in the REFEDIT working set. On enter we
  // seed this from the block definition's entities (re-parented to the
  // synthetic refedit space). User add/remove via REFSET grows/shrinks
  // the set. Anything not in the set is rendered tinted and ignored by
  // selection.
  refeditWorkingSet: z.array(z.string()).default([]),
  // JSON snapshot of the original block definition captured on enter so
  // a discard exit can restore it byte-perfect.
  refeditSnapshot: z.unknown().optional(),
  // Previous active space id (mirror of blockEditPreviousSpaceId — kept
  // separate for clarity since the two sessions can never co-exist).
  refeditPreviousSpaceId: z.string().optional(),
});

/**
 * Fast structural invariants check for a drawing document.
 *
 * Verifies:
 *   - each space id is unique
 *   - every entity.spaceId matches its parent space.id
 *   - every entity id is globally unique across all spaces
 *   - activeSpaceId refers to an existing space
 *
 * This does NOT validate Zod field shapes — for that, use
 * `drawingDocumentSchema.parse()`. Use this function for fast-path
 * updates where the input shape is already known-good (e.g. pure
 * coordinate transforms of already-validated entities) and only the
 * cross-entity invariants need to be re-checked.
 *
 * Exported so geometry reducers can skip the full Zod schema walk
 * on hot edit paths (move/copy/mirror/rotate/scale/stretch) while
 * keeping production-safe invariant enforcement.
 */
export function validateDrawingModel(document: DrawingDocument): void {
  const seenSpaceIds = new Set<string>();
  const seenEntityIds = new Set<string>();

  for (const space of document.spaces) {
    if (seenSpaceIds.has(space.id)) {
      throw new Error(`Duplicate space id: ${space.id}`);
    }

    seenSpaceIds.add(space.id);

    for (const entity of space.entities) {
      if (entity.spaceId !== space.id) {
        throw new Error(
          `Entity ${entity.id} belongs to ${entity.spaceId}, expected ${space.id}`,
        );
      }

      if (seenEntityIds.has(entity.id)) {
        throw new Error(`Duplicate entity id: ${entity.id}`);
      }

      seenEntityIds.add(entity.id);
    }
  }

  if (!seenSpaceIds.has(document.activeSpaceId)) {
    throw new Error(`Active space not found: ${document.activeSpaceId}`);
  }
}

/* ── XREF REGISTRY (Phase FIN.3) ───────────────────── */
// Per-xref metadata stored at the document level. Mirrors AutoCAD's
// XrefManager state: each entry tracks the external path, attachment
// type (attach vs overlay — overlays do not nest into other drawings),
// optional XCLIP boundary, and the timestamp of the last successful
// load. The key is the xref block name (matches blockDefinitionSchema
// name with bit-4/8 flag set).
export const xrefAttachmentTypeSchema = z.enum(["attach", "overlay"]);
export type XrefAttachmentType = z.infer<typeof xrefAttachmentTypeSchema>;

export const xrefClipBoundarySchema = z.object({
  type: z.enum(["rectangle", "polygon"]),
  vertices: z.array(drawingPointSchema).min(2),
});
export type XrefClipBoundary = z.infer<typeof xrefClipBoundarySchema>;

export const xrefRegistryEntrySchema = z.object({
  name: z.string().min(1),
  path: z.string(),
  status: z.enum(["resolved", "unloaded", "not-found"]).default("resolved"),
  attachmentType: xrefAttachmentTypeSchema.default("attach"),
  clipBoundary: xrefClipBoundarySchema.optional(),
  lastLoadedAt: z.number().optional(),
});
export type XrefRegistryEntry = z.infer<typeof xrefRegistryEntrySchema>;

// Default styleTables value used when a parsed document omits the field.
// NOTE: zod's `.default({})` does NOT recursively re-apply the inner
// schema defaults, so an empty `{}` would stay as literally `{}`. We
// instead build the default by parsing an empty object through the
// styleTables schema directly, which triggers every nested default
// (including the "Standard" TextStyle/DimensionStyle/MultileaderStyle).
//
// EXPORTED (BUG 4) — the SINGLE SOURCE OF TRUTH for the fresh-seed default
// records (layer "0" + the three "Standard" styles). `documentHasNoNonEntityHostState`
// (storeHelpers) compares a host's DEFAULT-named records VALUES against these so an
// EDITED default (a recolored layer "0", a "Standard" style with changed font /
// dimvars) is detected as host state, NOT mis-classified as a fresh seed.
export const defaultStyleTables = styleTablesSchema.parse({});

export const drawingDocumentSchema = z
  .object({
    id: z.string().min(1),
    title: z.string().min(1),
    spaces: z.array(drawingSpaceSchema).min(1),
    activeSpaceId: z.string().min(1),
    // ── In-memory importer provenance hint (DXF-CONFORMANT-SKELETON) ──
    // Set to `"imported"` by `importDxfStringToDrawingDocument` on every
    // document that originated from a parsed DXF file. A from-scratch document
    // (File → New: `buildInitialGeometryDocument`, fresh `drawingDocumentSchema
    // .parse({...})` constructions, AI/reducer builders) NEVER sets it.
    //
    // `ensureConformantSkeleton` (dxfBridge.conformantSkeleton.ts) uses this as
    // the AUTHORITATIVE from-scratch ⇔ imported discriminator: a content-only
    // heuristic was unsound in BOTH directions (a minimal ENTITIES-only import
    // carries no raw sections / block records → false "from-scratch" → the
    // skeleton corrupts the imported round-trip; a from-scratch doc gains
    // `blocks`/`blockRecords` the moment the user runs BLOCK → false "imported"
    // → it ships a non-conformant DXF). The explicit marker removes the
    // ambiguity: marker present ⇒ imported (pass-through), absent ⇒ from-scratch
    // (back-fill skeleton + hex-remint).
    //
    // This is an in-memory hint ONLY — it is NOT a DXF field and never reaches
    // the exported bytes (the writer reads no `provenance`). Survives the schema
    // parse (declared here) + the WBLOCK deep-clone (`JSON.parse(JSON.stringify
    // (...))` carries it, then `drawingDocumentSchema.parse` re-validates it).
    provenance: z.literal("imported").optional(),
    styleTables: styleTablesSchema.default(defaultStyleTables),
    // Block definitions parsed from the BLOCKS section, keyed by block
    // name. Used by the MLEADER renderer (contentType=1 / block content)
    // to walk child entities and by the CHPROP/DXF exporter to round-trip
    // the BLOCKS section verbatim. Empty by default so older documents
    // without blocks stay valid.
    blocks: z.record(z.string(), blockDefinitionSchema).default({}),
    // BLOCK_RECORD metadata mirrors the BLOCKS section but lives in the
    // TABLES section of the DXF. Keyed by the same block name. AutoCAD
    // always emits `*Model_Space` and `*Paper_Space` entries — the parser
    // seeds those automatically and user blocks are added on INSERT.
    blockRecords: z.record(z.string(), blockRecordSchema).default({}),
    // LTYPE (linetype) table, NAME-keyed (like `blocks`; lookups are
    // case-insensitive). Always carries the canonical acad.lin seed
    // (`STANDARD_LINETYPES`) — the reserved `ByLayer`/`ByBlock`/`Continuous` +
    // the standard linetypes — required so the future linetype refcheck
    // (PR-LT2, plan §10.5) can resolve standard names. PR-LT1 is schema + seed
    // only: no DXF parser populates custom linetypes yet, and no serializer
    // reads this field.
    //
    // Codex#2 P2 — MERGE, not replace-on-absent. A `.default` only seeds when
    // the field is ABSENT; a parsed/imported doc that supplies even ONE custom
    // LTYPE would otherwise DROP all the standard entries. `z.preprocess`
    // merges the seed UNDER the provided record (custom wins by name) so every
    // document always carries the standard names plus any custom ones. The
    // trailing `.default` keeps the absent-field case (fresh doc) seeded too.
    //
    // Codex#3 P2 — the merge is CASE-INSENSITIVE by name. Linetype names are
    // case-insensitive (AutoCAD), so a provided `Dashed` must REPLACE the seed's
    // `DASHED`, not coexist with it. An exact-key spread would keep BOTH (two
    // differently-cased keys), leaving a later case-insensitive refcheck/render
    // lookup ambiguous (two definitions for one name). We therefore drop any
    // seed entry whose name matches a provided entry case-insensitively, then
    // lay the provided record on top (provided wins, provided's key/casing kept).
    //
    // structuredClone per parse/preprocess: Zod passes a `.default` value
    // through by REFERENCE (no clone), and the PROD `assembleDrawingDocument`
    // path is an identity cast — so a shared `STANDARD_LINETYPES` would make
    // every document point at ONE seed object, and a future in-place edit
    // (PR-LT5 Linetype Manager) would corrupt the module const for all docs. A
    // fresh deep copy per document keeps each document's linetype table
    // independent.
    linetypes: z
      .preprocess(
        (v) => {
          // FIX-4 — tag the synthetic seed `source:"seed"` so a plain
          // round-trip can skip the unused standard linetypes (the seed must
          // still be present for the linetype refcheck — PR-LT1). A provided
          // record already carries its own `source` (`"file"` from the import
          // bridge, or absent → treated as emit-always by the exporter).
          const seed = seededStandardLinetypes();
          if (!v || typeof v !== "object") return seed;
          const provided = v as Record<string, unknown>;
          const providedLc = new Set(
            Object.keys(provided).map((k) => k.toLowerCase()),
          );
          const out: Record<string, unknown> = {};
          // Keep seed entries NOT overridden (case-insensitively) by `provided`.
          for (const [k, val] of Object.entries(seed)) {
            if (!providedLc.has(k.toLowerCase())) out[k] = val;
          }
          // Provided wins — provided's own key/casing is preserved.
          for (const [k, val] of Object.entries(provided)) out[k] = val;
          return out;
        },
        z.record(z.string(), linetypeSchema),
      )
      .default(() => seededStandardLinetypes()),
    // FIX-3 (Codex#2 PR-LT2 P2) — the LTYPE table-LEVEL header groups (table
    // handle `5` / owner `330` / subclass `100/AcDbSymbolTable` / max-entries
    // `70`, plus any vendor codes) that appear BETWEEN `2/LTYPE` and the first
    // `0/LTYPE` record. The parser captures this on
    // `DxfTablesSection.linetypeTableHeader`, but that section is transient: on
    // a pure MODEL round-trip (import → DrawingDocument → export, where the
    // document is the source of truth and the parsed section is gone) the
    // header would be lost and the writer would synthesise a minimal one with a
    // different table handle. Persisting it on the document preserves the real
    // header verbatim across the model round-trip. Excludes the `0/TABLE` +
    // `2/LTYPE` markers and the per-record / `0/ENDTAB` groups. Absent for
    // in-memory docs and files with no LTYPE table (the writer falls back to a
    // minimal synthesised header).
    linetypeTableHeader: z
      .array(
        z.object({
          code: z.number().int(),
          value: z.string(),
        }),
      )
      .optional(),
    // FIX-110 — the LAYER / STYLE / DIMSTYLE / BLOCK_RECORD table-LEVEL head
    // groups (table handle `5` / owner `330` / subclass `100/AcDbSymbolTable` /
    // max-entries `70`, plus vendor codes), keyed by table name. The writers for
    // these four typed tables previously DROPPED the head; a head-less table was
    // SILENTLY repaired by ezdxf `audit()` ("Fixed invalid table head handle in
    // table …") → AutoCAD recovery. Like `linetypeTableHeader`, the parser
    // captures this on the transient `DxfTablesSection.symbolTableHeaders`, but a
    // pure MODEL round-trip loses that section — persisting it here re-emits the
    // real `5/<handle>` head verbatim across the round-trip. From-scratch
    // documents adopt the conformant-skeleton template's heads through this same
    // field (ensureConformantSkeleton). Excludes the `0/TABLE` + `2/<name>`
    // markers and per-record / `0/ENDTAB` groups. Absent for in-memory docs and
    // files whose tables carried no head (the writer emits none).
    symbolTableHeaders: z
      .record(
        z.string(),
        z.array(
          z.object({
            code: z.number().int(),
            value: z.string(),
          }),
        ),
      )
      .optional(),
    // LAYOUT objects parsed from the OBJECTS section, keyed by layout
    // (tab) name. Each LAYOUT is owned by a `*Paper_Space`/`*Paper_Space<N>`
    // BLOCK_RECORD via a handle pair. Empty by default so older documents
    // without layouts stay valid.
    layouts: z.record(z.string(), layoutSchema).default({}),
    // Stable spaceId → Layout index built at import time. Keyed by the
    // disambiguated space id (e.g. "layout:Beta (1)") so Scene3DStage can
    // resolve page setup without stripping the "layout:" prefix and re-
    // matching by name — which collapses on duplicate-name drawings
    // (Codex P1 #162). Derived from `layouts` + spaces at import; not
    // serialised in commands or undo snapshots (optional with empty default).
    layoutsBySpaceId: z.record(z.string(), layoutSchema).default({}),
    // MC3 — Viewport entities keyed by spaceId (paper-space layout id).
    // Built at import time from space.entities where type === "Viewport";
    // rebuilt on merge. Analogous to layoutsBySpaceId but maps to the VP
    // entity array instead of the Layout page-setup record.
    // Optional with empty-object default so older documents stay valid.
    viewportsBySpaceId: z
      .record(z.string(), z.array(viewportEntitySchema))
      .default({}),
    // Drawing-level INSUNITS — the `$INSUNITS` DXF header variable. 0 =
    // Unitless, 1 = Inches, 4 = Millimeters, 6 = Meters, … (see
    // blockUnits.ts for codes 0-21). Drives Block Unit Factor auto-
    // scaling on INSERT. Defaults to 0 (Unitless) so legacy documents do
    // not retroactively apply scaling.
    // Ceiling is 21 (US Survey Feet), the highest code defined by the
    // Autodesk DXF $INSUNITS table / ezdxf `InsertUnits` enum
    // (AutoCAD 2017+ emits 21 for civil/survey drawings). Previously
    // capped at 20, which rejected valid survey DXFs at the schema gate
    // (Codex#248 F3 P2). No codes are documented beyond 21.
    insunits: z.number().int().min(0).max(21).default(0),
    // Drawing-level point display sysvars — the `$PDMODE` / `$PDSIZE` DXF
    // header variables (Batch-6 S-HEADER-PDMODE-PDSIZE). These are
    // document-level (apply to every POINT in the drawing), not per-entity.
    //   - pdmode ($PDMODE) — point display mode bitfield (0 = single dot,
    //                        2 = +, 3 = X, 4 = vertical tick; +32 circle,
    //                        +64 square). Default 0.
    //   - pdsize ($PDSIZE) — point display size. 0 = 5% of viewport height;
    //                        positive = absolute units; negative = % of
    //                        viewport. Default 0.
    // STORE arm only — the point-marker render arm (spatialBuilder) is
    // DEFUSE-owned and not wired here.
    pdmode: z.number().int().default(0),
    pdsize: z.number().default(0),
    // ── Drawing insertion base point — the `$INSBASE` HEADER variable ──
    // AutoCAD BASE command target. This is the origin AutoCAD uses when the
    // drawing is inserted as a block into another drawing (it does NOT move
    // any geometry — it only relocates the reference origin used on INSERT).
    // DXF HEADER group codes 10 (X) / 20 (Y) / 30 (Z) per the Autodesk DXF
    // reference + ezdxf `$INSBASE` (a Vec3 written with code 10). This is a
    // DIFFERENT field from the per-layout `Layout.insertionBase` (AcDbLayout
    // object, group codes 12/22/32). Defaults to (0,0,0) so legacy documents
    // without the var keep the AutoCAD default. Round-trips through the HEADER
    // via dxf-io `headerPointVars` (read on import, patched back on export);
    // mutated only through the Command Engine (`header/set-insertion-base`,
    // invertible via `computeInverse`).
    insertionBase: drawingPoint3DSchema.default({ x: 0, y: 0, z: 0 }),
    // ── Linetype-scale system variables (PDF-6.1) ──
    // Typed mirror of the DXF HEADER `$LTSCALE` / `$CELTSCALE` / `$PSLTSCALE`
    // / `$MSLTSCALE` variables (previously only round-tripped verbatim inside
    // `rawPassthroughSections`). Threaded into the PDF dash emitter so dashed
    // linetypes plot at the authored scale (closes the PDF-5c LTSCALE
    // deferral). All default to the AutoCAD defaults (1 / 1 / 1 / 1).
    //   - ltscale   ($LTSCALE)   — global linetype scale multiplier.
    //   - celtscale ($CELTSCALE) — current per-object scale for NEW entities
    //                              (the stored per-entity value is the entity
    //                              field `linetypeScale`, DXF group 48).
    //   - psltscale ($PSLTSCALE) — paper-space linetype scaling flag (0/1).
    //   - msltscale ($MSLTSCALE) — model-space annotative scaling flag (0/1).
    ltscale: z.number().default(1),
    celtscale: z.number().default(1),
    psltscale: z.number().int().default(1),
    msltscale: z.number().int().default(1),
    // ── Running object-snap system variable (OSNAP-HEADER-ROUNDTRIP) ──
    // Typed mirror of the DXF HEADER `$OSMODE` variable — the running
    // object-snap modes bitcode (1 = Endpoint, 2 = Midpoint, 4 = Center,
    // 8 = Node, 16 = Quadrant, 32 = Intersection, 64 = Insertion,
    // 128 = Perpendicular, 256 = Tangent, 512 = Nearest, 1024 = Geometric
    // Center, 2048 = Apparent Intersection, 4096 = Extension, 8192 = Parallel;
    // 16384 = osnap suppressed). DXF HEADER group code 70 (int16) per the
    // Autodesk DXF reference + ezdxf `$OSMODE` (code 70). Default 4133 =
    // Autodesk's documented OSMODE default (Endpoint+Center+Intersection+
    // Extension). ezdxf's new-doc default 20583 sets the 16384 osnap-suppressed
    // bit (ezdxf create-state, not AutoCAD's interactive default), so absent
    // `$OSMODE` must materialise 4133 to match AutoCAD (@codex #333).
    // Round-trips through the HEADER via dxf-io `headerSnapVars` (read on
    // import, patched back on export). STORE arm only — the runtime
    // object-snap engine reads its session sysvar (`sysvarSchema.ts`); this
    // typed field exists so the drawing-file value is not LOST on
    // import→export. NOTE: `$APERTURE` / `$AUTOSNAP` are NOT DXF header vars
    // (absent from ezdxf / Rust dxf crate / DXF R14 table) and are
    // deliberately not modelled here — they are runtime-only sysvars.
    osmode: z.number().int().default(4133),
    // ── MIRROR text-handling system variable ($MIRRTEXT) ──────────────
    // Typed mirror of the DXF HEADER `$MIRRTEXT` variable (group 70, int16).
    //   0 = mirrored TEXT/MTEXT stays READABLE — the insertion/alignment
    //       point reflects across the mirror axis but the glyph run is not
    //       flipped backwards/upside-down (AutoCAD's default).
    //   1 = text mirrors fully like ordinary geometry (backwards glyphs).
    // Default 0 = AutoCAD's documented MIRRTEXT default (verified vs Autodesk
    // system-variable reference + ezdxf, which leaves the TEXT generation-flag
    // backward/upside-down bits unset for MIRRTEXT=0). Round-trips through the
    // HEADER via dxf-io `headerDocumentVars` (read on import, patched back on
    // export). STORE arm: the reducer reads the value frozen onto the
    // `geometry/mirror-entities` command at dispatch (from the session sysvar
    // `sysvarSchema.ts` MIRRTEXT); this typed field exists so the drawing-file
    // value is not LOST on import→export.
    //
    // CLAMP to 0|1 (Codex #342 F4): AutoCAD's MIRRTEXT is STRICTLY a boolean
    // toggle — any non-zero value is treated as 1 (mirror fully), 0 stays 0.
    // A DXF carrying an out-of-range `$MIRRTEXT=2` therefore imports as 1, NOT
    // the raw 2. This keeps all three layers consistent: the imported document
    // field, the live sysvar store (`sysvarSchema.ts` `isValidSysvarValue`
    // accepts ONLY 0|1, so hydration would otherwise silently drop a raw 2 and
    // leave the live store stale), and the HEADER export (which re-emits the
    // typed field). Clamp-non-zero→1 (vs reject-to-0) preserves the user's
    // "mirror was on" intent — faithful to AutoCAD's boolean semantics. Shares
    // the `clampMirrtext` helper with the bridge import identity-cast path.
    mirrtext: z
      .number()
      .int()
      .transform((v) => clampMirrtext(v))
      .default(0),
    // ── CECOLOR current-entity-colour system variable ($CECOLOR) ──────
    // Typed mirror of the DXF HEADER `$CECOLOR` variable (group 62, one
    // signed int). This is AutoCAD's CURRENT colour — the colour assigned
    // to NEWLY-created entities (the COLOR / -COLOR / SETCOLOR / ЦВЕТ
    // command sets it). Unlike the numeric sysvars above (MIRRTEXT /
    // OSMODE — plain ints), CECOLOR is the typed `EntityColor` UNION:
    // ByLayer / ByBlock / index / true-colour / colour-book. `$CECOLOR`
    // packs the whole union into ONE int via the AcCmEntityColor raw-color
    // encoding (256/absent = ByLayer = the default → OMITTED on export; 0 =
    // ByBlock; 1..255 = ACI; a NEGATIVE C2-raw-color = true colour) — the
    // codec lives in `color.ts` (`cecolorFromHeader` / `cecolorToHeader`),
    // verified against the Autodesk DXF reference + ezdxf `colors.py`.
    //
    // Round-trips through the HEADER via dxf-io `headerDocumentVars` (read
    // on import → `cecolorFromHeader`; patched back on export ←
    // `cecolorToHeader`, spec `defaultValue: 256` omits a standalone
    // ByLayer). STORE arm: the live current-colour value is held in the
    // sibling zustand `cecolorStore` (a union cannot ride the integer-only
    // sysvar schema); this typed field exists so the drawing-file value is
    // not LOST on import→export, and the `header/set-cecolor` reducer
    // writes it (invertible undo). Default ByLayer = AutoCAD's out-of-the-
    // box CECOLOR.
    cecolor: entityColorSchema.default({ kind: "byLayer" }),
    // ── Solid-fill display system variable ($FILLMODE) ────────────────
    // Typed mirror of the DXF HEADER `$FILLMODE` variable (group 70, int16).
    //   1 = hatches and fills, 2D solids and WIDE POLYLINES are drawn SOLID
    //       (Autodesk system-variable reference: type Integer, initial value
    //       1, saved in the drawing);
    //   0 = the same objects are drawn as OUTLINES only.
    // `ezdxf` `sections/headervars.py` declares the identical code/default
    // pair (`$FILLMODE`, code 70, default 1, DXF12-2018).
    //
    // This is a DRAWING-level display switch, not per-entity styling: a wide
    // POLYLINE / LWPOLYLINE carries only its group 40/41/43 WIDTHS, and
    // whether the resulting band is painted or merely outlined is decided
    // here. The renderer projects it (`scene3d` — `RenderableEntity.fillmode`,
    // the band fill mesh and the band pick), and the vector plot already
    // assumed the default-1 state before this field existed
    // (`print/pdf/converters/widePolylineBand.ts`).
    //
    // Round-trips through the HEADER via dxf-io `headerDocumentVars` (read on
    // import, patched back on export with spec `defaultValue: 1`, so a
    // default-1 source is never invented a `$FILLMODE` while a present one is
    // overwritten in place). Clamped to 0|1 on BOTH import paths through the
    // shared `clampFillmode`, because the prod import builds the document on
    // an identity-cast path that skips this Zod walk.
    fillmode: z
      .number()
      .int()
      .transform((v) => clampFillmode(v))
      .default(DEFAULT_FILLMODE),
    // ── Annotation scale catalogue (Phase FIN.2 — annotative blocks) ──
    // Mirrors AutoCAD's `$CANNOSCALE`/`$CANNOSCALEVALUE` header pair plus
    // the named ACDB_ANNOTATIONSCALES dictionary. `cannoscaleName` is the
    // user-facing label ("1:50", "1:100", …) currently selected from the
    // status-bar dropdown; `cannoscale` is its numeric scale factor (50,
    // 100, …). `annotationScales` is the catalogue of named scales the
    // user can switch between. Annotative INSERTs are hidden when the
    // current `cannoscaleName` is not in their `annotationScales` list.
    cannoscale: z.number().default(1),
    cannoscaleName: z.string().default("1:1"),
    annotationScales: z
      .array(
        z.object({
          name: z.string().min(1),
          scaleFactor: z.number().positive(),
        }),
      )
      .default([]),
    // ── Annotative system variables (PR-1) ──
    // Mirrors AutoCAD's `$ANNOALLVISIBLE`, `$ANNOAUTOSCALE`, and
    // `$ANNOMONITOR` header variables.
    //
    // - `annoAllVisible` — when true (default), entities without a
    //   context for the current annotation scale are still drawn using
    //   their master fields. When false, they are hidden — matches
    //   AutoCAD's status-bar "Show annotations at all scales" toggle.
    // - `annoAutoScale` — auto-add context behaviour. Spec range -4..4
    //   per Autodesk reference: positive values auto-add the current
    //   scale to annotative entities when CANNOSCALE changes (default
    //   4 = "add current annotation scale to all annotative objects").
    //   Negative values disable the behaviour (sign indicates whether
    //   the prior state should be restored when toggling). 0 is also
    //   a valid disabled value.
    // - `annoMonitor` — when true, AutoCAD highlights entities whose
    //   current cannoscale doesn't match any of their contexts.
    annoAllVisible: z.boolean().default(true),
    annoAutoScale: z.number().int().min(-4).max(4).default(4),
    annoMonitor: z.boolean().default(false),
    // XREF registry (Phase FIN.3) — keyed by xref block name. Empty by
    // default; populated by XATTACH and updated by every other xref
    // command (XBIND removes entries, XUNLOAD/XRELOAD update status).
    xrefRegistry: z.record(z.string(), xrefRegistryEntrySchema).default({}),
    // Raw DXF groups for unsupported ENTITIES-section siblings (SPLINE,
    // ACAD_TABLE, XRECORD, …) kept verbatim for full-fidelity round-trip
    // (E2 fidelity restoration). Parsed by importDxfDocumentToDrawingDocument
    // from parser DxfUnsupportedEntity records; emitted by
    // exportDrawingDocumentToDxfDocument into the ENTITIES section before
    // ENDSEC. See also BlockDefinition.rawEntityGroups for the BLOCKS-section
    // analogue. Optional so in-memory DrawingDocument partials (tests, AI
    // command builders) keep compiling; the import pipeline always sets it.
    rawEntityGroups: z.array(rawEntityGroupSchema).optional(),
    // Raw DXF group stream for OBJECTS-section records we do not yet
    // model structurally (MATERIAL, VISUALSTYLE, SCALE, SORTENTSTABLE,
    // XRECORD, GROUP, PLOTSETTINGS, …). АБИ1.dxf has ~500k of these
    // groups — dropping them halves round-trip byte count. Appended
    // verbatim after the structured layouts / dynamic-block objects.
    rawObjectGroups: z
      .array(
        z.object({
          code: z.number().int(),
          value: z.string(),
        }),
      )
      .optional(),
    // AW-0 foundation — typed AcDbAssocArray OBJECTS graph carrier (docs/
    // MASTERPLAN.md AW-0). Threaded through the bridge INSTEAD of flattening
    // the graph into the opaque `rawObjectGroups` catch-all above (dropped
    // from AW-0 onward — see dxfBridge.import.ts): every associative array's
    // full ACDBASSOCNETWORK / ACDBASSOCACTION / ACDBASSOCARRAYACTIONBODY /
    // ACDBASSOCARRAYRECTANGULARPARAMETERS / ACDBASSOCDEPENDENCY subgraph,
    // self-describing (decoded fields) yet still carrying each record's OWN
    // verbatim `rawGroups` for the byte-stable round-trip channel
    // (`assocArrayWriter.ts#serializeAssocArrayObject`, unchanged by this
    // slice). See `dxfBridge.arrayAssocExport.ts` for the export-side
    // by-construction dirty-diff choke point this carrier feeds.
    rawAssocArrayObjects: z.array(assocArrayCarrierObjectSchema).optional(),
    // Raw DXF group stream for TABLES-section entries we do not model
    // (VPORT, VIEW, UCS, APPID, DIMSTYLE extra records, …). Appended
    // verbatim after the structured text/dim/layer/blockRecord tables.
    rawTableGroups: z
      .array(
        z.object({
          code: z.number().int(),
          value: z.string(),
        }),
      )
      .optional(),
    // Saved view extracted from the VPORT `*Active` record. AutoCAD
    // applies this on open so the user sees the same framing they left;
    // we parse it here so the scene loader can do the same instead of
    // forcing zoom-extents. The raw groups still round-trip verbatim via
    // `rawTableGroups`, so this field is read-only metadata.
    savedView: savedViewSchema.optional(),
    // Raw DXF sections we preserve verbatim (HEADER, CLASSES, ACDSDATA,
    // THUMBNAILIMAGE, …). These sections carry drawing-level metadata
    // that the bridge does not model structurally — without this pass-
    // through the round-trip drops hundreds of KB of header variables
    // (`$ACADVER`, `$INSBASE`, `$INSUNITS`, …) and class definitions
    // AutoCAD needs to parse the output.
    rawPassthroughSections: z
      .array(
        z.object({
          name: z.string().min(1),
          groups: z.array(
            z.object({
              code: z.number().int(),
              value: z.string(),
            }),
          ),
        }),
      )
      .optional(),
    // Import-time audit telemetry — counts of OBJECTS-section references whose
    // handle chain DANGLED during the second-pass resolvers (annotative
    // contexts, associative arrays, viewport clip). The raw records still
    // round-trip verbatim through the rawObjectGroups / spatialFilters
    // passthroughs; these counts are read-only diagnostics for the import audit
    // log / UI (never affect geometry or export). Each sub-count is present
    // ONLY when non-zero so a clean import carries no field at all. Optional so
    // in-memory DrawingDocument partials (tests, AI builders, reducers) keep
    // compiling; the import pipeline sets it only when an orphan was counted.
    importDiagnostics: z
      .object({
        orphanAnnotativeScaleReferences: z
          .number()
          .int()
          .nonnegative()
          .optional(),
        orphanAssocArrayReferences: z.number().int().nonnegative().optional(),
        orphanViewportClipReferences: z.number().int().nonnegative().optional(),
      })
      .optional(),
  })
  .superRefine((document, ctx) => {
    try {
      validateDrawingModel(document as DrawingDocument);
    } catch (error) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message:
          error instanceof Error ? error.message : "Invalid drawing model",
      });
    }
  });

/**
 * Fast-path helper for reducers that mutate a subset of entities in an
 * already-validated drawing document without changing the field shape
 * of any entity (pure coordinate transforms, rename operations, etc).
 *
 * In **non-production** (development and tests) runs full
 * `drawingDocumentSchema.parse()` so structural bugs surface during
 * authoring / CI. In **production** runs lightweight `validateDrawingModel()`
 * invariant check + cast. Skips per-entity Zod schema walk.
 *
 * **Identity preservation contract:** in production this returns the input
 * reference unchanged (no allocation, no clone). Reducers and cache layer
 * rely on this for incremental update without diff. In dev/test Zod parse
 * deep-clones; tests must verify data correctness, NOT reference identity.
 *
 * Uses `import.meta.env.PROD` (Vite-native, statically replaced at build
 * time → guaranteed dead-code elimination) instead of `process.env.NODE_ENV`
 * to avoid ambiguity around bundler env-var replacement behavior.
 *
 * Safe to use only when every entity in `next` was produced from an
 * already-parsed entity by a transform that does not change the field
 * shape. Use `drawingDocumentSchema.parse` for any path that adds new
 * entities from scratch or accepts user-shaped data.
 */
export function assembleDrawingDocument(next: unknown): DrawingDocument {
  if (import.meta.env.PROD) {
    validateDrawingModel(next as DrawingDocument);
    return next as DrawingDocument;
  }
  return drawingDocumentSchema.parse(next);
}

/**
 * Incremental fast-path assembler for **in-place** entity edits (T6c).
 *
 * `assembleDrawingDocument` re-validates the WHOLE document on every call:
 * in production an O(N) `validateDrawingModel` Set-walk over every space +
 * entity, and in dev/test a full `drawingDocumentSchema.parse` deep-parse of
 * the entire document. Because that runs on EVERY mutation — move / rotate /
 * scale / stretch / property / grip / attribute edits, not just create — the
 * cost grows O(N) per op and becomes janky on 10k+/685k-entity drawings.
 *
 * For an **in-place** edit that does NOT add or remove any entity and does
 * NOT change any entity id or spaceId (nor add/remove/rename a space, nor
 * change activeSpaceId), `validateDrawingModel`'s invariants — unique entity
 * ids, `entity.spaceId === space.id`, unique space ids, `activeSpaceId`
 * present — are **preserved by construction**. The O(N) walk is therefore
 * unnecessary work, so:
 *   - In **production** (`import.meta.env.PROD`) this returns `next` directly
 *     (no walk, no allocation) — invariants hold by construction.
 *   - In **dev / test** it re-runs the per-entity Zod schema check
 *     (`drawingEntitySchema.parse`) for ONLY the entities whose ids are in
 *     `changedEntityIds` (property edits translate string payloads → typed
 *     patches, so the per-entity field validation MUST survive — @codex
 *     #390-F4), leaving every unchanged entity by reference and dropping only
 *     the whole-document parse/walk. The document-level schema defaults
 *     (linetype-seed merge, mirrtext clamp, color coercion, …) are idempotent
 *     on an already-parsed document, so re-applying them buys nothing and is
 *     skipped.
 *
 * **CONTRACT — the caller MUST guarantee, relative to the previously
 * assembled document, that the edit:**
 *   - adds NO entity and removes NO entity (the entity SET is unchanged);
 *   - changes NO entity `id`;
 *   - changes NO entity `spaceId`;
 *   - adds, removes, or renames NO space (the space SET / ids are unchanged);
 *   - leaves `activeSpaceId` unchanged.
 * Any edit that violates this (creates, deletes, explode, copy, array,
 * space-moves that re-parent an entity, block create/explode, doc-level
 * styleTables-only mutations, …) MUST use `assembleDrawingDocument` instead —
 * the full path re-validates cross-entity invariants and re-applies
 * doc-level defaults. Using this fast-path for a set-changing edit would
 * silently bypass fail-closed invariant enforcement (e.g. a duplicate-id
 * create would no longer throw).
 *
 * `changedEntityIds` is the command's OWN target ids (no diff is computed).
 * If it is empty, `next` is returned unchanged (dev/test included) — nothing
 * to re-validate.
 *
 * Like `assembleDrawingDocument`, the production path returns the input
 * reference unchanged (no allocation); the dev/test path rebuilds only the
 * spaces that contain a changed entity (unchanged spaces and unchanged
 * entities pass through by reference). Tests must verify data correctness,
 * NOT reference identity.
 */
export function assembleDrawingDocumentInPlace(
  next: DrawingDocument,
  changedEntityIds: ReadonlyArray<string>,
): DrawingDocument {
  if (import.meta.env.PROD) {
    // Invariants (unique ids, entity.spaceId===space.id, unique space ids,
    // activeSpaceId present) are preserved by construction for an in-place
    // edit — skip the O(N) validateDrawingModel walk entirely.
    return next;
  }
  if (changedEntityIds.length === 0) {
    return next;
  }
  const changed = new Set(changedEntityIds);
  // Re-run the per-entity Zod field check ONLY for changed entities; leave
  // every other entity (and every space with no changed entity) by reference.
  const spaces = next.spaces.map((space) => {
    let touched = false;
    const entities = space.entities.map((entity) => {
      if (!changed.has(entity.id)) {
        return entity;
      }
      touched = true;
      return drawingEntitySchema.parse(entity);
    });
    if (!touched) {
      return space;
    }
    return { ...space, entities };
  });
  return { ...next, spaces };
}

export type {
  DrawingEntityBase,
  DrawingPoint,
  DrawingPoint3D,
} from "./entityBase";
export {
  DXF_POLYLINE_FLAG,
  DXF_VERTEX_FLAG,
  dxfGroupRecordSchema,
  dxfVertexSubtype,
  oleFrameEntitySchema,
  polylineEntitySchema,
  polylineHasContinuousLinetype,
  polylineIsClosed,
  polylineIsClosedN,
  polylineSeqendMetadataSchema,
  polylineSubtype,
  polylineVertexRecordSchema,
  tableEntitySchema,
  tableRotationDegrees,
} from "./entityPolylineTableOle";
export type {
  DxfGroupRecord,
  OleFrameEntity,
  PolylineEntity,
  PolylineSeqendMetadata,
  PolylineSubtype,
  PolylineVertexRecord,
  TableEntity,
} from "./entityPolylineTableOle";

export type LineEntity = z.infer<typeof lineEntitySchema>;
export type PointEntity = z.infer<typeof pointEntitySchema>;
export type CircleEntity = z.infer<typeof circleEntitySchema>;
export type ArcEntity = z.infer<typeof arcEntitySchema>;
export type EllipseEntity = z.infer<typeof ellipseEntitySchema>;
export type SplineEntity = z.infer<typeof splineEntitySchema>;
export type PolylineVertex = z.infer<typeof polylineVertexSchema>;
export type LwpolylineEntity = z.infer<typeof lwpolylineEntitySchema>;
export type TextEntity = z.infer<typeof textEntitySchema>;
export type MtextEntity = z.infer<typeof mtextEntitySchema>;
export type DimensionEntity = z.infer<typeof dimensionEntitySchema>;
export type DimensionStyleOverrides = DimensionEntity["styleOverrides"];
export type MleaderBreakPoint = z.infer<typeof mleaderBreakPointSchema>;
export type MleaderLeaderLine = z.infer<typeof mleaderLeaderLineSchema>;
export type MleaderLeaderNode = z.infer<typeof mleaderLeaderNodeSchema>;
export type MleaderLeaderRoot = z.infer<typeof mleaderLeaderRootSchema>;
export type MultileaderEntity = z.infer<typeof multileaderEntitySchema>;
export type RevisionCloudEntity = z.infer<typeof revisionCloudEntitySchema>;
export type WipeoutEntity = z.infer<typeof wipeoutEntitySchema>;
export type HatchBoundaryEdge = z.infer<typeof hatchBoundaryEdgeSchema>;
export type HatchBoundaryPath = z.infer<typeof hatchBoundaryPathSchema>;
export type HatchGradientColor = z.infer<typeof hatchGradientColorSchema>;
export type HatchGradient = z.infer<typeof hatchGradientSchema>;
export type HatchPatternLine = z.infer<typeof hatchPatternLineSchema>;
export type HatchEntity = z.infer<typeof hatchEntitySchema>;
export type BlockInsertEntity = z.infer<typeof blockInsertEntitySchema>;
export type BlockInsertAttribute = z.infer<typeof blockInsertAttributeSchema>;
export type ArrayRectEntity = z.infer<typeof arrayRectEntitySchema>;
export type {
  ArrayAssocItem,
  ArrayItemLocator,
  ArrayItemTransform,
  ArrayItemSourceMapping,
  ArrayItemOverride,
  ArrayReplacedItem,
  AssocArrayCarrierItem,
  AssocArrayCarrierObject,
  AssocArrayCarrierRectParams,
  AssocArrayCarrierValueParam,
  AssocArrayHandles,
  AssocArrayParamsSnapshot,
} from "./arrayAssoc";
export type ViewportEntity = z.infer<typeof viewportEntitySchema>;
export type AttdefEntity = z.infer<typeof attdefEntitySchema>;
export type BlockChildEntity = z.infer<typeof blockChildEntitySchema>;
export type BlockDefinition = z.infer<typeof blockDefinitionSchema>;
export type DrawingEntity = z.infer<typeof drawingEntitySchema>;
/**
 * Input shape for `drawingEntitySchema` — fields with `default(...)` (such
 * as `layer`, `color`, `linetype`, `lineweight`, `transparency`, `thickness`)
 * are optional. Use this for entity-literal arguments that the schema will
 * later complete via `parse()`. Phase 7.2 PR-E3 (#39).
 */
export type DrawingEntityInput = z.input<typeof drawingEntitySchema>;
export type DrawingSpaceKind = z.infer<typeof drawingSpaceKindSchema>;
export type DrawingSpace = z.infer<typeof drawingSpaceSchema>;
export type TextStyle = z.infer<typeof textStyleSchema>;
export type DimensionStyle = z.infer<typeof dimensionStyleSchema>;
export type MultileaderStyle = z.infer<typeof multileaderStyleSchema>;
export type StyleTables = z.infer<typeof styleTablesSchema>;
export type DrawingDocument = z.infer<typeof drawingDocumentSchema>;
export type DrawingModel = DrawingDocument;
// (SavedView is re-exported above next to its schema.)

/**
 * Entity-type names whose per-type schema carries a `thickness` field (DXF
 * group 39). ENUMERATED VERBATIM from the per-type schema arms, and PINNED
 * against them by a schema-walking guard (`./thicknessBearingRegistry.test.ts`)
 * rather than by a second hand-written list — one truth
 * shared by the property-apply document translator
 * (`apps/web/.../propertyTranslator.mergeCommonFieldsForType`) and the undo
 * inverse snapshot reader (`command-engine/.../geometryMutations.readCommonProps`)
 * so neither clones (and silently drifts from) the list. Patching `thickness`
 * onto a type whose schema lacks it would pollute the typed entity (dev/test
 * `assembleDrawingDocument` Zod re-parse strips the unknown key; PROD does not
 * re-parse so the stray key lingers but never exports — either way wrong, so we
 * never write it):
 *   z.number().default(0): Line · Point · Circle · Arc · Ellipse · LWPOLYLINE ·
 *     Text · Revision Cloud · Polyline
 *   z.number().optional(): Dimension (code 39)
 * NOT carried by: MText, Multileader, Block/Insert, Spline, Hatch, Wipeout,
 * Viewport, Rectangular Associative Array, Table (ACAD_TABLE) and OLE Frame
 * (OLE2FRAME) — none has a `thickness` schema field, matching the DXF
 * reference, which defines no group 39 on `AcDbTable` or `AcDbOle2Frame`.
 *
 * SCAN THE WHOLE PACKAGE, NOT JUST THIS FILE (@codex PR #785 round 17, P2). The
 * heavy `POLYLINE` arm (`polylineEntitySchema`, `./entityPolylineTableOle.ts`)
 * declares `thickness: z.number().default(0)` exactly like the nine arms that
 * live in THIS file, but the list was enumerated from `model.ts` alone,
 * so it was omitted — and with it the Thickness row collapsed to a hard-coded
 * `ui-projection-deferred` "0" in `buildCommonDescriptors`, the typed common
 * writer refused every edit, and `optionalCommonKeyDefault` minted no undo
 * default, while the importer, the DXF writer and the renderer's group-39 sweep
 * all carried the real value. Any future per-type arm added outside `model.ts`
 * belongs here too.
 *
 * SUBTYPE NOTE for the heavy polyline: only the group-70 `2d` subtype is DRAWN
 * with an extrusion surface (`scene3d/adapters/typed/polylineFromEntity.ts`),
 * because Autodesk's OCS section lists 3DPOLYLINE and MESH among the entities
 * that "do not lie in a particular plane" and states that "of these entities,
 * only lines and points can be extruded". That is a RENDER rule, not a storage
 * or a palette rule: group 39 is a legal, round-tripped field on all four
 * subtypes (ezdxf declares `thickness` on `acdb_polyline`, not per subtype),
 * and AutoCAD's own CHANGE help says of its `Thickness` option: "Changing the
 * thickness of a 3D polyline, dimension, or layout viewport object has no
 * effect" — NO EFFECT, not refused, which is the same wording that already
 * governs Dimension in this very set. So the row stays readable and writable
 * for the whole type (hiding it would hide real file data), and the subtype
 * gate lives where the surface is built.
 *
 * Capability gating (a thickness-bearing type whose CHPROP capability still
 * EXCLUDES Thickness, e.g. Dimension / Revision Cloud) is applied SEPARATELY at
 * each write site — this set is only the schema-carries-the-field truth.
 */
export const THICKNESS_BEARING_ENTITY_TYPES: ReadonlySet<string> = new Set([
  "Line",
  "Point",
  "Circle",
  "Arc",
  "Ellipse",
  "LWPOLYLINE",
  "Text",
  "Dimension",
  "Revision Cloud",
  // Heavy POLYLINE — `polylineEntitySchema` in `./entityPolylineTableOle.ts`.
  "Polyline",
]);

// ── Annotative discriminated-union re-exports (PR-1) ──
// Re-exported here so `@dxf-ai-editor/drawing-model` consumers can
// import `AnnotScaleContext` from the same module surface as
// `TextEntity` etc. — `index.ts` does `export *` so all variants are
// transitively reachable; this alias keeps existing model.ts-style
// imports working.
export type { AnnotScaleContext } from "./annotative";
// PR-7 — also re-export the Zod schema value (not just the type) so
// reducer/dialog code can validate raw `entity/add-annotation-context`
// payloads (which arrive as `unknown` across the contracts boundary —
// see editor.ts:1592 for the rationale of keeping contracts decoupled
// from drawing-model).
export { annotScaleContextSchema } from "./annotative";

// ── LTYPE (linetype) re-exports (PR-LT1) ──
// Re-export the schema value + inferred type from `linetype.ts` so consumers
// can import `Linetype` / `linetypeSchema` from the same model.ts surface as
// `BlockDefinition` etc. (mirrors the `annotScaleContextSchema` pattern). The
// canonical acad.lin seed is exported from `linetypeSeed.ts` via the barrel.
export { linetypeSchema } from "./linetype";
export type { Linetype } from "./linetype";
